import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LogOut, Plus, Trash2, CreditCard, Banknote, History, TrendingUp, Check, Edit2, FileText, X, Save, Minus, Settings, MapPin, Receipt, Home, Printer, Wifi, WifiOff, Package, AlertTriangle, StickyNote, ExternalLink, Calendar, ClipboardList, Star, Clock, Wallet, Split, Volume2, VolumeX, Bell, Globe, Menu, Upload, Camera, FolderOpen, Landmark, Sparkles, Loader2, Info, HelpCircle, Gift, UserPlus, User, UserCheck, Search, Phone, Mail, Wrench } from 'lucide-react';
import SummaryCard from '@/components/SummaryCard';
import ModificationHistoryModal from '@/components/ModificationHistoryModal';
import ExpenseModal from '@/components/ExpenseModal';
import TwoFactorAuthModal from '@/components/TwoFactorAuthModal';
import AdminSettingsModal from '@/components/AdminSettingsModal';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { printTicketViaQz, getQzStatus, listPrinters } from '@/utils/qzTrayService';
import useNotifications from '@/hooks/useNotifications';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Composant InfoTooltip pour expliquer les calculs
const InfoTooltip = ({ title, description, formula }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="relative inline-block">
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        className="w-5 h-5 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white/70 hover:text-white transition-colors"
        title="Cliquez pour plus d'infos"
      >
        <Info className="w-3 h-3" />
      </button>
      {isOpen && (
        <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-72 p-3 bg-slate-900 text-white text-xs rounded-lg shadow-xl">
          <div className="font-semibold text-sm mb-1 text-blue-300">{title}</div>
          <p className="text-slate-300 mb-2">{description}</p>
          {formula && (
            <div className="bg-slate-800 rounded px-2 py-1 font-mono text-[10px] text-green-300">
              📊 {formula}
            </div>
          )}
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-slate-900"></div>
        </div>
      )}
    </div>
  );
};

// Configuration imprimante POS par défaut
const DEFAULT_PRINTER_CONFIG = {
  shopName: 'MPHONE',
  address: '5 Rue Paul Verlaine',
  city: '69100 Villeurbanne',
  phone: '04 78 XX XX XX',
  ticketWidth: 58, // mm (58mm ou 80mm standard POS)
  fontSize: 12,
  showLogo: false,
  footerMessage: 'Merci de votre visite !',
  useQzTray: true, // Activer l'impression directe via QZ Tray
  printerName: '' // Nom de l'imprimante POS (auto-détection si vide)
};

export default function DashboardPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [entries, setEntries] = useState([]);
  
  // Lire la date depuis l'URL ou utiliser aujourd'hui
  const urlDate = searchParams.get('date');
  const initialDate = urlDate && /^\d{4}-\d{2}-\d{2}$/.test(urlDate) 
    ? urlDate 
    : format(new Date(), 'yyyy-MM-dd');
  
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [newEntry, setNewEntry] = useState({
    description: '',
    quantity: '',
    purchase_price: '',
    sale_price: '',
    payment_method: '', // 'cash', 'card', 'mixed', 'installment'
    cash_amount: '',    // Pour paiement mixte
    card_amount: '',    // Pour paiement mixte
    installment_count: '' // Pour paiement en plusieurs fois
  });
  const [summary, setSummary] = useState({
    total_revenue: 0,
    total_cash: 0,
    total_card: 0,
    total_mixed: 0,
    total_margin: 0,
    cash_count: 0,
    card_count: 0,
    mixed_count: 0
  });
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [historyModal, setHistoryModal] = useState({ isOpen: false, entryId: null, history: [], description: '' });
  const [isCashRegisterClosed, setIsCashRegisterClosed] = useState(false);
  const [showSummary, setShowSummary] = useState(true); // Always show summary
  const [reopenCount, setReopenCount] = useState(0);
  const [totalReopens, setTotalReopens] = useState(0); // Total cumulé de toutes les réouvertures
  const [cashRegisterHistory, setCashRegisterHistory] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentModalData, setPaymentModalData] = useState({
    mode: '', // 'cash', 'card', 'mixed', 'installment'
    cashAmount: '',
    cardAmount: '',
    installmentCount: 2,
    installmentPaid: 1, // Nombre de versements déjà payés
    // Nouveaux champs pour paiement par points/avoir/promo
    usePoints: false,
    pointsAmount: '',
    useAvoir: false,
    avoirAmount: '',
    usePromo: false,
    promoAmount: '',
    promoDescription: '',
    selectedClient: null
  });
  const [nonValidatedHighlight, setNonValidatedHighlight] = useState([]);
  
  // Recherche client pour paiement mixte avec points
  const [mixedPaymentClientSearch, setMixedPaymentClientSearch] = useState('');
  const [mixedPaymentSearchResults, setMixedPaymentSearchResults] = useState([]);
  const [mixedPaymentSearching, setMixedPaymentSearching] = useState(false);
  
  // Onglet Annulations
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingEntryId, setCancellingEntryId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelledEntries, setCancelledEntries] = useState([]);
  const [activeTab, setActiveTab] = useState('entries'); // 'entries' ou 'cancelled'
  
  // Double authentification states
  const [showMondayAuth, setShowMondayAuth] = useState(false);
  const [mondayAuthVerified, setMondayAuthVerified] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  
  // Printer configuration state
  const [printerConfig, setPrinterConfig] = useState(() => {
    const saved = localStorage.getItem('printerConfig');
    return saved ? JSON.parse(saved) : DEFAULT_PRINTER_CONFIG;
  });
  const [showPrinterModal, setShowPrinterModal] = useState(false);
  
  // QZ Tray state
  const [qzStatus, setQzStatus] = useState({ available: false, connected: false, printers: [] });
  const [availablePrinters, setAvailablePrinters] = useState([]);
  
  // Notifications temps réel et badges
  const { badgeCounts, isConnected: wsConnected, lastWhatsAppMessage, refresh: refreshBadges, toggleSound } = useNotifications();
  const [soundEnabled, setSoundEnabled] = useState(true);
  
  // External requests state (PhoneLab Admin)
  const [externalRequests, setExternalRequests] = useState({
    hasNewRequests: false,
    requestCount: 0,
    lastCheck: null
  });
  
  // Check if button should blink based on last visit
  const [shouldBlinkAdmin, setShouldBlinkAdmin] = useState(() => {
    const lastVisit = localStorage.getItem('phonelabAdminLastVisit');
    if (!lastVisit) return true; // Never visited, should blink
    const timeSinceLastVisit = Date.now() - parseInt(lastVisit);
    const oneHour = 60 * 60 * 1000;
    return timeSinceLastVisit > oneHour; // Blink if more than 1 hour since last visit
  });

  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Document import modal (clone exact de Comptabilité)
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [accountingCategories, setAccountingCategories] = useState([]);
  const [documentForm, setDocumentForm] = useState({
    title: '',
    category_id: 'cat_achat',
    amount: '',
    date: new Date().toISOString().slice(0, 10),
    supplier: '',
    reference: '',
    notes: '',
    file_data: null,
    file_name: null,
    file_type: null
  });
  const [analyzingDocument, setAnalyzingDocument] = useState(false);
  const [aiAnalysisResult, setAiAnalysisResult] = useState(null);
  const [autoAnalyze, setAutoAnalyze] = useState(true);
  const [savingDocument, setSavingDocument] = useState(false);

  // ===== SYSTÈME FIDÉLITÉ ENCAISSEMENTS =====
  const [showLoyaltyModal, setShowLoyaltyModal] = useState(false);
  const [loyaltyEntryId, setLoyaltyEntryId] = useState(null);
  const [loyaltyClients, setLoyaltyClients] = useState([]);
  const [loyaltyClientSearch, setLoyaltyClientSearch] = useState('');
  const [loyaltySelectedClient, setLoyaltySelectedClient] = useState(null);
  const [loyaltyMode, setLoyaltyMode] = useState(null); // 'existing', 'none', 'new'
  const [loyaltyNewClient, setLoyaltyNewClient] = useState({
    name: '',
    phone: '',
    email: '',
    address: ''
  });
  const [loyaltySearchResults, setLoyaltySearchResults] = useState([]);
  const [loyaltySearching, setLoyaltySearching] = useState(false);
  const [loyaltyAddresses, setLoyaltyAddresses] = useState([]);
  const [loyaltyAddressLoading, setLoyaltyAddressLoading] = useState(false);
  // Garde la liaison entry -> client
  const [entryLoyaltyMap, setEntryLoyaltyMap] = useState({});
  // Track les points calculés par entry
  const [entryPointsPreview, setEntryPointsPreview] = useState({});

  const token = localStorage.getItem('token');

  // Vérifier le statut de QZ Tray au chargement
  useEffect(() => {
    const checkQzTray = async () => {
      const status = await getQzStatus();
      setQzStatus(status);
      if (status.connected) {
        setAvailablePrinters(status.printers);
      }
    };
    // Attendre que QZ Tray soit chargé
    const timer = setTimeout(checkQzTray, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    check2FAStatus();
  }, []);

  useEffect(() => {
    if (mondayAuthVerified || !showMondayAuth) {
      fetchEntries();
      fetchExpenses();
      fetchCancelledEntries();
      checkCashRegisterStatus();
      fetchTotalReopens();
      loadAccountingCategories(); // Charger les catégories pour le modal Doc
      // Les badges sont maintenant gérés via useNotifications()
    }
  }, [selectedDate, mondayAuthVerified]);

  // Synchroniser l'URL avec la date sélectionnée
  useEffect(() => {
    const currentUrlDate = searchParams.get('date');
    if (selectedDate !== currentUrlDate) {
      if (selectedDate === format(new Date(), 'yyyy-MM-dd')) {
        // Si c'est aujourd'hui, on enlève le paramètre date de l'URL
        searchParams.delete('date');
      } else {
        searchParams.set('date', selectedDate);
      }
      setSearchParams(searchParams, { replace: true });
    }
  }, [selectedDate, searchParams, setSearchParams]);

  // Réagir aux changements d'URL (bouton retour du navigateur)
  useEffect(() => {
    const urlDate = searchParams.get('date');
    if (urlDate && /^\d{4}-\d{2}-\d{2}$/.test(urlDate) && urlDate !== selectedDate) {
      setSelectedDate(urlDate);
    }
  }, [searchParams]);

  // Affichage du reste à payer calculé (pas de pré-remplissage automatique)
  // L'admin remplit manuellement les champs Espèces/Carte

  const check2FAStatus = async () => {
    // Authentification hebdomadaire désactivée
    setMondayAuthVerified(true);
    setShowMondayAuth(false);
  };

  const handleMondayAuthSuccess = () => {
    setShowMondayAuth(false);
    setMondayAuthVerified(true);
    toast.success('Authentification hebdomadaire réussie !');
  };

  // Charger les catégories comptables pour le modal Doc
  const loadAccountingCategories = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/accounting/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccountingCategories(response.data);
    } catch (error) {
      console.error('Error loading accounting categories:', error);
    }
  };

  // Fonction de sauvegarde document (clone exact de Comptabilité)
  const saveDocumentFromModal = async () => {
    if (!documentForm.title) {
      toast.error('Veuillez saisir un titre pour le document');
      return;
    }
    if (!documentForm.date) {
      toast.error('Veuillez sélectionner une date');
      return;
    }

    setSavingDocument(true);
    
    try {
      const finalAmount = parseFloat(documentForm.amount) || 0;
      
      const payload = {
        title: documentForm.title,
        category_id: documentForm.category_id || 'cat_achat',
        amount: finalAmount,
        date: documentForm.date,
        supplier: documentForm.supplier || '',
        reference: documentForm.reference || '',
        notes: documentForm.notes || '',
        file_data: documentForm.file_data || null,
        file_name: documentForm.file_name || null,
        file_type: documentForm.file_type || null
      };
      
      console.log('=== SAVING DOCUMENT FROM MODAL ===');
      console.log('Payload:', JSON.stringify(payload, null, 2));

      const response = await axios.post(`${BACKEND_URL}/api/accounting/documents`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Create response:', response.data);
      toast.success('✅ Document enregistré dans Comptabilité !');
      toast.info('📂 Allez dans Comptabilité pour voir le document', { duration: 4000 });
      
      // Fermer et réinitialiser
      setShowDocumentModal(false);
      setAiAnalysisResult(null);
      setDocumentForm({
        title: '',
        category_id: 'cat_achat',
        amount: '',
        date: new Date().toISOString().slice(0, 10),
        supplier: '',
        reference: '',
        notes: '',
        file_data: null,
        file_name: null,
        file_type: null
      });
    } catch (error) {
      console.error('=== ERREUR SAUVEGARDE ===');
      console.error('Error:', error);
      console.error('Response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingDocument(false);
    }
  };

  const fetchTotalReopens = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cash-register/total-reopens`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTotalReopens(response.data.total_reopens || 0);
    } catch (error) {
      console.error('Error fetching total reopens:', error);
    }
  };

  // Vérification des demandes externes (PhoneLab Admin)
  const checkExternalRequests = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/external-requests/check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setExternalRequests({
          hasNewRequests: response.data.has_new_requests,
          requestCount: response.data.request_count,
          lastCheck: response.data.last_check
        });
      }
    } catch (error) {
      console.error('Error checking external requests:', error);
    }
  };

  // Marquer les demandes comme vues
  const markExternalRequestsSeen = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/external-requests/mark-seen`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExternalRequests(prev => ({ ...prev, hasNewRequests: false }));
    } catch (error) {
      console.error('Error marking requests as seen:', error);
    }
  };

  // Ouvrir la page admin externe et marquer comme vu
  const openExternalAdmin = () => {
    localStorage.setItem('phonelabAdminLastVisit', Date.now().toString());
    setShouldBlinkAdmin(false);
    markExternalRequestsSeen();
    window.open('https://www.phonelab69.fr/admin/devis', '_blank');
  };

  // Vérification périodique des demandes externes (toutes les 60 secondes)
  useEffect(() => {
    if (mondayAuthVerified || !showMondayAuth) {
      checkExternalRequests();
      const interval = setInterval(checkExternalRequests, 60000); // 60 secondes
      return () => clearInterval(interval);
    }
  }, [mondayAuthVerified]);

  const fetchExpenses = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/expenses?date=${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpenses(response.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  };

  const checkCashRegisterStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cash-register/status?date=${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsCashRegisterClosed(response.data.is_closed || false);
      setReopenCount(response.data.reopen_count || 0);
      
      // Fetch history if register exists
      if (response.data.date) {
        const historyResponse = await axios.get(`${BACKEND_URL}/api/cash-register/history?date=${selectedDate}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCashRegisterHistory(historyResponse.data);
      }
    } catch (error) {
      setIsCashRegisterClosed(false);
      setReopenCount(0);
      setCashRegisterHistory([]);
    }
  };

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/entries?date=${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data);
      calculateSummary(response.data);
      
      // Initialiser entryLoyaltyMap SEULEMENT pour les entrées qui ont des données fidélité
      // Ne pas écraser les attributions en mémoire qui n'ont pas encore été sauvegardées
      const loyaltyMap = {};
      const pointsMap = {};
      response.data.forEach(entry => {
        if (entry.loyalty_client_id) {
          loyaltyMap[entry.id] = {
            mode: 'existing',
            client: { id: entry.loyalty_client_id, name: entry.loyalty_client_name },
            points: entry.loyalty_points_earned || 0
          };
          pointsMap[entry.id] = entry.loyalty_points_earned || 0;
        } else if (entry.loyalty_skipped === true) {
          // Fidélité explicitement ignorée
          loyaltyMap[entry.id] = { mode: 'none', client: null, points: 0 };
          pointsMap[entry.id] = 0;
        }
        // Si pas de données fidélité dans la DB, ne pas écraser l'attribution en mémoire
      });
      // Merger au lieu de remplacer - garder les attributions en mémoire
      setEntryLoyaltyMap(prev => {
        const merged = { ...prev };
        Object.keys(loyaltyMap).forEach(key => {
          merged[key] = loyaltyMap[key];
        });
        return merged;
      });
      setEntryPointsPreview(prev => {
        const merged = { ...prev };
        Object.keys(pointsMap).forEach(key => {
          merged[key] = pointsMap[key];
        });
        return merged;
      });
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    }
  };

  const fetchCancelledEntries = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/entries/cancelled?date=${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCancelledEntries(response.data);
    } catch (error) {
      console.error('Error fetching cancelled entries:', error);
    }
  };

  const calculateSummary = (entriesData) => {
    const total_revenue = entriesData.reduce((sum, e) => sum + (e.sale_price * e.quantity), 0);
    
    // Calculer les totaux en tenant compte des paiements mixtes
    let total_cash = 0;
    let total_card = 0;
    let total_mixed = 0;
    
    // Compteurs de transactions
    let cash_count = 0;
    let card_count = 0;
    let mixed_count = 0;
    
    entriesData.forEach(e => {
      const amount = e.sale_price * e.quantity;
      
      if (e.payment_method === 'cash') {
        total_cash += amount;
        cash_count++;
      } else if (e.payment_method === 'card') {
        total_card += amount;
        card_count++;
      } else if (e.payment_method === 'mixed' && e.payment_details) {
        // Pour les paiements mixtes, utiliser les montants détaillés
        total_cash += e.payment_details.cash_amount || e.cash_amount || 0;
        total_card += e.payment_details.card_amount || e.card_amount || 0;
        total_mixed += amount;
        mixed_count++;
      } else if (e.payment_method === 'installment' && e.payment_details) {
        // Pour les paiements échelonnés, calculer le montant déjà payé
        const perInstallment = amount / (e.payment_details.installment_count || 1);
        total_cash += perInstallment * (e.payment_details.installment_paid || 1);
        cash_count++;
      } else {
        // Fallback: utiliser cash_amount et card_amount si disponibles
        total_cash += e.cash_amount || 0;
        total_card += e.card_amount || 0;
      }
    });
    
    const total_margin = entriesData.reduce((sum, e) => sum + e.margin, 0);
    setSummary({ 
      total_revenue, 
      total_cash, 
      total_card, 
      total_mixed,
      total_margin,
      cash_count,
      card_count,
      mixed_count
    });
  };

  // Print receipt/ticket function
  // Fonction pour sauvegarder la config imprimante
  const savePrinterConfig = (config) => {
    setPrinterConfig(config);
    localStorage.setItem('printerConfig', JSON.stringify(config));
    toast.success('Configuration imprimante sauvegardée');
  };

  // Impression ticket de caisse format POS
  const printTicket = async (entry) => {
    const cfg = printerConfig;
    
    // Si QZ Tray est activé et disponible, essayer l'impression directe
    if (cfg.useQzTray && qzStatus.available) {
      toast.loading('Impression en cours...', { id: 'print-ticket' });
      
      const result = await printTicketViaQz(entry, cfg, cfg.printerName);
      
      if (result.success) {
        toast.success(`✅ ${result.message}`, { id: 'print-ticket' });
        return;
      } else {
        // Fallback vers impression navigateur si QZ Tray échoue
        toast.warning(`⚠️ ${result.message} - Ouverture du ticket dans le navigateur...`, { id: 'print-ticket' });
      }
    }
    
    // Fallback: impression via fenêtre navigateur
    printTicketFallback(entry);
  };

  // Impression fallback via fenêtre navigateur
  const printTicketFallback = (entry) => {
    const cfg = printerConfig;
    const width = cfg.ticketWidth === 80 ? '72mm' : '48mm';
    const fontSize = cfg.fontSize || 14;
    const ticketWindow = window.open('', '_blank', 'width=300,height=600');
    
    const ticketContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Ticket</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          @page { 
            size: ${cfg.ticketWidth}mm auto; 
            margin: 0; 
          }
          body {
            font-family: 'Courier New', 'Lucida Console', monospace;
            font-size: ${fontSize}px;
            font-weight: bold;
            color: #000000;
            width: ${width};
            margin: 0 auto;
            padding: 3mm;
            line-height: 1.4;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .center { text-align: center; }
          .right { text-align: right; }
          .bold { font-weight: 900 !important; }
          .big { font-size: ${fontSize + 6}px; font-weight: 900; }
          .extra-big { font-size: ${fontSize + 10}px; font-weight: 900; }
          .small { font-size: ${fontSize}px; font-weight: bold; }
          .line { border-bottom: 2px dashed #000000; margin: 5px 0; }
          .double-line { border-bottom: 3px solid #000000; margin: 5px 0; }
          .row { display: flex; justify-content: space-between; margin: 3px 0; font-weight: bold; }
          .header { margin-bottom: 8px; }
          .total-section { margin-top: 8px; padding-top: 4px; }
          .footer { margin-top: 10px; padding-top: 6px; }
          .shop-name { font-size: ${fontSize + 8}px; font-weight: 900; letter-spacing: 1px; }
          .item-name { font-size: ${fontSize + 2}px; font-weight: 900; }
          .total-amount { font-size: ${fontSize + 10}px; font-weight: 900; }
          @media print {
            body { width: ${width}; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header center">
          <div class="shop-name">${cfg.shopName}</div>
          <div class="bold">${cfg.address}</div>
          <div class="bold">${cfg.city}</div>
          <div class="bold">Tel: ${cfg.phone}</div>
        </div>
        
        <div class="line"></div>
        
        <div class="center big">TICKET DE CAISSE</div>
        <div class="center bold">
          ${new Date(entry.created_at).toLocaleDateString('fr-FR')} ${formatTime(entry.created_at)}
        </div>
        
        <div class="line"></div>
        
        <div class="row">
          <span class="item-name">${entry.description}</span>
        </div>
        <div class="row bold">
          <span>${entry.quantity} x ${formatCurrency(entry.sale_price)}</span>
          <span>${formatCurrency(entry.sale_price * entry.quantity)}</span>
        </div>
        
        <div class="double-line"></div>
        
        <div class="total-section">
          <div class="row total-amount">
            <span>TOTAL</span>
            <span>${formatCurrency(entry.sale_price * entry.quantity)}</span>
          </div>
          <div class="row big">
            <span>Mode:</span>
            <span class="bold">${entry.payment_method === 'cash' ? 'ESPECES' : 'CB'}</span>
          </div>
        </div>
        
        <div class="line"></div>
        
        <div class="footer center bold">
          <div>${cfg.footerMessage}</div>
          <div>A bientôt !</div>
        </div>
        
        <div class="no-print center" style="margin-top:20px;">
          <button onclick="window.print()" style="padding:10px 20px;font-size:14px;cursor:pointer;">
            🖨️ Imprimer
          </button>
        </div>
        
        <script>
          // Auto-print si possible
          setTimeout(() => {
            try { window.print(); } catch(e) {}
          }, 500);
        </script>
      </body>
      </html>
    `;
    ticketWindow.document.write(ticketContent);
    ticketWindow.document.close();
  };

  // Rafraîchir la liste des imprimantes
  const refreshPrinters = async () => {
    const status = await getQzStatus();
    setQzStatus(status);
    if (status.connected) {
      setAvailablePrinters(status.printers);
      toast.success(`${status.printers.length} imprimante(s) détectée(s)`);
    } else {
      toast.error('QZ Tray non connecté');
    }
  };

  // Ouvrir le modal de paiement
  const openPaymentModal = () => {
    const total = parseFloat(newEntry.sale_price || 0) * parseFloat(newEntry.quantity || 1);
    setPaymentModalData({
      mode: newEntry.payment_method || '',
      cashAmount: newEntry.payment_method === 'mixed' ? (newEntry.cash_amount || '') : '',
      cardAmount: newEntry.payment_method === 'mixed' ? (newEntry.card_amount || '') : '',
      installmentCount: newEntry.payment_method === 'installment' ? parseInt(newEntry.installment_count || 2) : 2,
      installmentPaid: 1,
      totalAmount: total,
      // Réinitialiser les options de remise
      usePoints: false,
      pointsAmount: '',
      useAvoir: false,
      avoirAmount: '',
      usePromo: false,
      promoAmount: '',
      promoDescription: '',
      selectedClient: null
    });
    setShowPaymentModal(true);
  };

  // Confirmer le mode de paiement
  const confirmPaymentMethod = async () => {
    const { 
      mode, cashAmount, cardAmount, installmentCount, installmentPaid,
      usePoints, pointsAmount, useAvoir, avoirAmount, usePromo, promoAmount, promoDescription,
      selectedClient
    } = paymentModalData;
    
    if (!mode) {
      toast.error('Sélectionnez un mode de paiement');
      return;
    }
    
    const totalSale = parseFloat(newEntry.sale_price || 0) * parseFloat(newEntry.quantity || 1);
    const pointsDeduction = usePoints ? parseFloat(pointsAmount || 0) : 0;
    const avoirDeduction = useAvoir ? parseFloat(avoirAmount || 0) : 0;
    const promoDeduction = usePromo ? parseFloat(promoAmount || 0) : 0;
    const totalDeductions = pointsDeduction + avoirDeduction + promoDeduction;
    const remainingAmount = totalSale - totalDeductions;
    
    // Validation: si on utilise des points ou une promo, le client doit être sélectionné
    if ((usePoints || usePromo) && !selectedClient) {
      toast.error('Veuillez sélectionner un client pour utiliser les points ou une promo');
      return;
    }
    
    // Validation: les points doivent être suffisants
    if (usePoints && selectedClient) {
      const clientPoints = selectedClient.points_balance || 0;
      const pointsRequired = Math.ceil(pointsDeduction * 20); // 5€ = 100pts, donc 1€ = 20pts
      if (clientPoints < pointsRequired) {
        toast.error(`Points insuffisants. Le client a ${clientPoints} pts, il faut ${pointsRequired} pts pour ${pointsDeduction}€`);
        return;
      }
    }
    
    if (mode === 'mixed') {
      const cash = parseFloat(cashAmount) || 0;
      const card = parseFloat(cardAmount) || 0;
      
      if (Math.abs((cash + card) - remainingAmount) > 0.01) {
        toast.error(`Le total Espèces + CB (${(cash + card).toFixed(2)}€) doit correspondre au montant restant (${remainingAmount.toFixed(2)}€)`);
        return;
      }
    }
    
    // Mettre à jour l'entrée avec le mode de paiement
    const updatedEntry = {
      ...newEntry,
      payment_method: mode,
      cash_amount: mode === 'mixed' ? cashAmount : '',
      card_amount: mode === 'mixed' ? cardAmount : '',
      installment_count: mode === 'installment' ? installmentCount : '',
      installment_paid: mode === 'installment' ? installmentPaid : ''
    };
    
    setShowPaymentModal(false);
    
    // Soumettre directement l'entrée après sélection du paiement
    try {
      const payload = {
        date: selectedDate,
        description: updatedEntry.description,
        quantity: parseInt(updatedEntry.quantity || 1),
        purchase_price: parseFloat(updatedEntry.purchase_price || 0),
        sale_price: parseFloat(updatedEntry.sale_price),
        payment_method: mode
      };

      if (mode === 'mixed') {
        payload.payment_details = {
          type: 'mixed',
          cash_amount: parseFloat(cashAmount || 0),
          card_amount: parseFloat(cardAmount || 0),
          // Ajout des déductions
          points_deduction: pointsDeduction,
          avoir_deduction: avoirDeduction,
          promo_deduction: promoDeduction,
          client_id: selectedClient?.id || null,
          client_name: selectedClient?.name || null
        };
      } else if (mode === 'installment') {
        payload.payment_details = {
          type: 'installment',
          installment_count: parseInt(installmentCount || 2),
          installment_paid: parseInt(installmentPaid || 1)
        };
      }

      await axios.post(`${BACKEND_URL}/api/entries`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Créer les sorties de caisse pour les remises
      if (pointsDeduction > 0 && selectedClient) {
        // Créer la sortie pour remise points
        await axios.post(`${BACKEND_URL}/api/expenses`, {
          description: `Remise points fidélité - ${selectedClient.name}`,
          amount: pointsDeduction,
          category: 'remise_points'
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Déduire les points du client
        // Conversion: 100 pts = 5€, donc 1€ = 20pts
        const pointsToDeduct = Math.ceil(pointsDeduction * 20); // 1€ = 20pts
        await axios.post(`${BACKEND_URL}/api/loyalty/points/redeem`, {
          client_id: selectedClient.id,
          points_to_use: pointsToDeduct,
          redemption_type: 'fixed',
          sale_amount: totalSale,
          category: 'autre'
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success(`${pointsToDeduct} points déduits du solde de ${selectedClient.name}`);
      }
      
      if (avoirDeduction > 0) {
        // Créer la sortie pour avoir
        await axios.post(`${BACKEND_URL}/api/expenses`, {
          description: `Utilisation avoir${selectedClient ? ` - ${selectedClient.name}` : ''}`,
          amount: avoirDeduction,
          category: 'remise_avoir'
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      if (promoDeduction > 0 && selectedClient) {
        // Créer la sortie pour promo
        await axios.post(`${BACKEND_URL}/api/expenses`, {
          description: `Remise promo${promoDescription ? ` (${promoDescription})` : ''} - ${selectedClient.name}`,
          amount: promoDeduction,
          category: 'remise_promo'
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      toast.success('Encaissement ajouté !');
      setNewEntry({
        description: '',
        quantity: '',
        purchase_price: '',
        sale_price: '',
        payment_method: '',
        cash_amount: '',
        card_amount: '',
        installment_count: '',
        installment_paid: ''
      });
      // Reset des champs remise
      setPaymentModalData(prev => ({
        ...prev,
        usePoints: false,
        pointsAmount: '',
        useAvoir: false,
        avoirAmount: '',
        usePromo: false,
        promoAmount: '',
        promoDescription: '',
        selectedClient: null
      }));
      setMixedPaymentClientSearch('');
      setMixedPaymentSearchResults([]);
      
      fetchEntries();
      fetchExpenses(); // Rafraîchir les sorties
      
      // Auto-focus immédiat sur le champ description pour nouvelle saisie
      setTimeout(() => {
        const descInput = document.getElementById('description-input');
        if (descInput) {
          descInput.focus();
          descInput.select();
        }
      }, 50);
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleAddEntry = async (e) => {
    e.preventDefault();
    
    // Validations strictes
    if (!newEntry.description) {
      toast.error('⚠️ Description obligatoire');
      return;
    }
    
    if (!newEntry.sale_price) {
      toast.error('⚠️ Prix de vente obligatoire');
      return;
    }
    
    if (!newEntry.payment_method) {
      toast.error('⚠️ Sélectionnez le mode de paiement', {
        duration: 4000
      });
      return;
    }

    try {
      // Construire le payload avec les données de paiement
      const payload = {
        date: selectedDate,
        description: newEntry.description,
        quantity: parseInt(newEntry.quantity || 1),
        purchase_price: parseFloat(newEntry.purchase_price || 0),
        sale_price: parseFloat(newEntry.sale_price),
        payment_method: newEntry.payment_method
      };

      // Ajouter les données spécifiques selon le mode de paiement
      if (newEntry.payment_method === 'mixed') {
        payload.payment_details = {
          type: 'mixed',
          cash_amount: parseFloat(newEntry.cash_amount || 0),
          card_amount: parseFloat(newEntry.card_amount || 0)
        };
      } else if (newEntry.payment_method === 'installment') {
        payload.payment_details = {
          type: 'installment',
          installment_count: parseInt(newEntry.installment_count || 2),
          installment_paid: parseInt(newEntry.installment_paid || 1)
        };
      }

      await axios.post(`${BACKEND_URL}/api/entries`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Encaissement ajouté !');
      setNewEntry({
        description: '',
        quantity: '',
        purchase_price: '',
        sale_price: '',
        payment_method: '',
        cash_amount: '',
        card_amount: '',
        installment_count: '',
        installment_paid: ''
      });
      fetchEntries();
      
      // Auto-focus immédiat sur le champ description pour nouvelle saisie
      setTimeout(() => {
        const descInput = document.getElementById('description-input');
        if (descInput) {
          descInput.focus();
          descInput.select();
        }
      }, 50);
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleValidateAndNext = async (entryId, currentStatus) => {
    await handleValidateEntry(entryId, currentStatus);
    // Auto-focus immédiat sur le champ description pour nouvelle saisie
    setTimeout(() => {
      const descInput = document.getElementById('description-input');
      if (descInput) {
        descInput.focus();
        descInput.select();
      }
    }, 100);
  };

  const handleCloseCashRegister = async () => {
    if (entries.length === 0) {
      toast.error('Aucune transaction à clôturer');
      return;
    }

    // Check if there's an entry being edited
    if (editingEntry) {
      toast.error('⚠️ Fermeture impossible', {
        description: 'Veuillez enregistrer ou annuler la modification en cours'
      });
      return;
    }

    // Check if all entries are validated
    const nonValidatedEntries = entries.filter(e => e.status !== 'validated');
    if (nonValidatedEntries.length > 0) {
      toast.error('⚠️ Fermeture impossible', {
        description: `${nonValidatedEntries.length} ligne(s) non validée(s). Validez toutes les lignes avant de fermer.`,
        duration: 5000
      });
      
      // Highlight non-validated entries
      setNonValidatedHighlight(nonValidatedEntries.map(e => e.id));
      setTimeout(() => setNonValidatedHighlight([]), 5000);
      return;
    }
    
    // NOTE: Vérification fidélité DÉSACTIVÉE - l'attribution est optionnelle

    try {
      await axios.post(`${BACKEND_URL}/api/cash-register/close?date=${selectedDate}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Caisse fermée avec succès !', {
        description: 'Un email récapitulatif a été envoyé'
      });
      
      setIsCashRegisterClosed(true);
      checkCashRegisterStatus();
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la fermeture');
    }
  };

  // ===== FONCTIONS SYSTÈME FIDÉLITÉ ENCAISSEMENTS =====
  
  // Ouvrir le modal fidélité pour une ligne d'encaissement
  const openLoyaltyModal = (entryId) => {
    setLoyaltyEntryId(entryId);
    setShowLoyaltyModal(true);
    setLoyaltyMode(null);
    setLoyaltySelectedClient(null);
    setLoyaltyClientSearch('');
    setLoyaltySearchResults([]);
    setLoyaltyNewClient({ name: '', phone: '', email: '', address: '' });
    setLoyaltyAddresses([]);
  };
  
  // Recherche rapide de client
  const searchLoyaltyClients = async (query) => {
    if (!query || query.length < 2) {
      setLoyaltySearchResults([]);
      return;
    }
    
    setLoyaltySearching(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/loyalty/clients?search=${encodeURIComponent(query)}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoyaltySearchResults(response.data || []);
    } catch (error) {
      console.error('Erreur recherche clients:', error);
      setLoyaltySearchResults([]);
    }
    setLoyaltySearching(false);
  };
  
  // Debounce de la recherche client
  useEffect(() => {
    if (loyaltyClientSearch.length >= 2) {
      const timeoutId = setTimeout(() => {
        searchLoyaltyClients(loyaltyClientSearch);
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      setLoyaltySearchResults([]);
    }
  }, [loyaltyClientSearch]);
  
  // Recherche client pour paiement mixte avec points
  const searchMixedPaymentClients = async (query) => {
    if (!query || query.length < 2) {
      setMixedPaymentSearchResults([]);
      return;
    }
    
    setMixedPaymentSearching(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/loyalty/clients?search=${encodeURIComponent(query)}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMixedPaymentSearchResults(response.data || []);
    } catch (error) {
      console.error('Erreur recherche clients:', error);
      setMixedPaymentSearchResults([]);
    }
    setMixedPaymentSearching(false);
  };
  
  // Debounce recherche client paiement mixte
  useEffect(() => {
    if (mixedPaymentClientSearch.length >= 2) {
      const timeoutId = setTimeout(() => {
        searchMixedPaymentClients(mixedPaymentClientSearch);
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      setMixedPaymentSearchResults([]);
    }
  }, [mixedPaymentClientSearch]);
  
  // Recherche d'adresse (API gouv.fr)
  const searchAddress = async (query) => {
    if (!query || query.length < 5) {
      setLoyaltyAddresses([]);
      return;
    }
    
    setLoyaltyAddressLoading(true);
    try {
      const response = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=5`);
      const data = await response.json();
      setLoyaltyAddresses(data.features?.map(f => ({
        label: f.properties.label,
        city: f.properties.city,
        postcode: f.properties.postcode
      })) || []);
    } catch (error) {
      console.error('Erreur recherche adresse:', error);
    }
    setLoyaltyAddressLoading(false);
  };
  
  // Sélectionner un client existant
  const selectLoyaltyClient = (client) => {
    setLoyaltySelectedClient(client);
    setLoyaltyMode('existing');
    setLoyaltyClientSearch('');
    setLoyaltySearchResults([]);
  };
  
  // Créer un nouveau client pour cette ligne
  const handleCreateLoyaltyClient = async () => {
    if (!loyaltyNewClient.name.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }
    if (!loyaltyNewClient.phone.trim()) {
      toast.error('Le téléphone est obligatoire');
      return;
    }
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/loyalty/clients/create`, {
        name: loyaltyNewClient.name,
        phone: loyaltyNewClient.phone,
        email: loyaltyNewClient.email || '',
        notes: loyaltyNewClient.address || '',
        loyalty_tier: 'standard',
        sms_marketing: true,  // Activé pour notification bienvenue
        email_marketing: true  // Activé pour notification bienvenue
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const newClient = response.data.client;
        setLoyaltySelectedClient(newClient);
        setLoyaltyMode('new');
        toast.success(`Client "${newClient.name}" créé !`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur création client');
    }
  };
  
  // Valider le choix fidélité pour cette ligne
  const confirmLoyaltyChoice = async () => {
    const entry = entries.find(e => e.id === loyaltyEntryId);
    if (!entry) return;
    
    const totalAmount = entry.sale_price * entry.quantity;
    const pointsToEarn = Math.floor(totalAmount); // 1€ = 1 point
    
    if (loyaltyMode === 'none') {
      // Pas de compte - ignorer
      setEntryLoyaltyMap(prev => ({
        ...prev,
        [loyaltyEntryId]: { mode: 'none', client: null, points: 0 }
      }));
      setEntryPointsPreview(prev => ({
        ...prev,
        [loyaltyEntryId]: 0
      }));
      
      // Sauvegarder dans l'entrée que la fidélité a été ignorée
      try {
        await axios.put(`${BACKEND_URL}/api/entries/${loyaltyEntryId}`, {
          loyalty_skipped: true,
          loyalty_client_id: null
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {
        console.error('Erreur mise à jour entrée:', e);
      }
      
      toast.success('Fidélité ignorée pour cette ligne');
    } else if (loyaltySelectedClient) {
      // Client sélectionné ou créé
      try {
        // Enregistrer les points via l'API
        await axios.post(`${BACKEND_URL}/api/loyalty/clients/${loyaltySelectedClient.id}/manual-action`, {
          client_id: loyaltySelectedClient.id,
          action_type: 'purchase',
          amount: totalAmount,
          category: 'autre',
          description: entry.description,
          reason: `Encaissement caisse #${entry.id?.slice(-6) || 'N/A'}`,
          add_visit: true,
          calculate_points: true
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setEntryLoyaltyMap(prev => ({
          ...prev,
          [loyaltyEntryId]: { 
            mode: loyaltyMode, 
            client: loyaltySelectedClient, 
            points: pointsToEarn 
          }
        }));
        setEntryPointsPreview(prev => ({
          ...prev,
          [loyaltyEntryId]: pointsToEarn
        }));
        
        // Mettre à jour l'entrée pour marquer que la fidélité a été attribuée
        try {
          await axios.put(`${BACKEND_URL}/api/entries/${loyaltyEntryId}`, {
            loyalty_client_id: loyaltySelectedClient.id,
            loyalty_client_name: loyaltySelectedClient.name,
            loyalty_points_earned: pointsToEarn
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (e) {
          console.error('Erreur mise à jour entrée fidélité:', e);
        }
        
        toast.success(`+${pointsToEarn} pts pour ${loyaltySelectedClient.name} !`, {
          description: `Nouveau solde: ${(loyaltySelectedClient.points_balance || 0) + pointsToEarn} pts`
        });
      } catch (error) {
        toast.error('Erreur lors de l\'attribution des points');
        return;
      }
    } else {
      toast.error('Veuillez sélectionner une option');
      return;
    }
    
    setShowLoyaltyModal(false);
    setLoyaltyEntryId(null);
  };

  const handleAddExpense = async (expenseData) => {
    try {
      const payload = {
        date: selectedDate,
        description: expenseData.description,
        amount: parseFloat(expenseData.amount),
        payment_method: expenseData.payment_method,
        category: expenseData.category
      };

      await axios.post(`${BACKEND_URL}/api/expenses`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Sortie de caisse ajoutée !');
      setShowExpenseModal(false);
      fetchExpenses();
      fetchEntries(); // Refresh to update totals
    } catch (error) {
      toast.error('Erreur lors de l\'ajout de la sortie');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/expenses/${expenseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sortie supprimée');
      fetchExpenses();
      fetchEntries();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleReopenCashRegister = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/cash-register/reopen?date=${selectedDate}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Caisse réouverte !', {
        description: 'Vous pouvez maintenant modifier les entrées'
      });
      
      setIsCashRegisterClosed(false);
      checkCashRegisterStatus();
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réouverture');
    }
  };

  // Ouvrir le modal d'annulation
  const openCancelModal = (entryId) => {
    setCancellingEntryId(entryId);
    setCancelReason('');
    setShowCancelModal(true);
  };

  // Confirmer l'annulation avec motif
  const handleConfirmCancel = async () => {
    if (!cancelReason || cancelReason.trim().length < 3) {
      toast.error('Le motif d\'annulation est obligatoire (minimum 3 caractères)');
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/entries/${cancellingEntryId}/cancel`, {
        reason: cancelReason.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Ligne annulée et archivée');
      setShowCancelModal(false);
      setCancellingEntryId(null);
      setCancelReason('');
      fetchEntries();
      fetchCancelledEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
  };

  // Fonction de suppression redirigée vers annulation
  const handleDeleteEntry = async (entryId) => {
    openCancelModal(entryId);
  };

  const handleValidateEntry = async (entryId, currentStatus) => {
    // ===== FIDÉLITÉ OPTIONNELLE (plus d'obligation) =====
    // L'admin peut valider sans avoir attribué les points
    // Les points sont attribués manuellement si souhaité
    
    try {
      const response = await axios.patch(`${BACKEND_URL}/api/entries/${entryId}/validate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.status === 'validated' ? 'Ligne validée !' : 'Validation annulée');
      fetchEntries();
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const startEdit = (entry) => {
    setEditingEntry(entry.id);
    setEditForm({
      description: entry.description,
      quantity: entry.quantity,
      purchase_price: entry.purchase_price,
      sale_price: entry.sale_price,
      payment_method: entry.payment_method
    });
  };

  const cancelEdit = () => {
    setEditingEntry(null);
    setEditForm({});
  };

  const saveEdit = async (entryId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/entries/${entryId}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Modification enregistrée !', {
        description: 'Les changements ont été sauvegardés'
      });
      setEditingEntry(null);
      setEditForm({});
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  const viewHistory = async (entryId, description) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/entries/${entryId}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistoryModal({
        isOpen: true,
        entryId,
        history: response.data,
        description
      });
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'historique');
    }
  };

  const formatTime = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '--:--';
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
  };

  return (
    <div className="min-h-screen bg-black" data-testid="dashboard-page">
      {/* Header responsive */}
      <header className="sticky top-0 z-50 bg-gray-900 border-b border-gray-700">
        {/* Desktop Header */}
        <div className="hidden md:flex h-11 items-center justify-between px-2">
          {/* Gauche - Admin + Son */}
          <div className="flex items-center gap-1">
            <Button
              onClick={openExternalAdmin}
              data-testid="external-admin-button"
              size="sm"
              className={`relative h-7 px-2 ${
                badgeCounts.quote_requests > 0 || shouldBlinkAdmin
                  ? 'bg-red-500 text-white animate-pulse'
                  : 'bg-blue-600 text-white'
              }`}
            >
              <Bell className="w-3 h-3" />
              {badgeCounts.quote_requests > 0 && (
                <span className="absolute -top-1 -right-1 bg-white text-red-600 text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {badgeCounts.quote_requests}
                </span>
              )}
            </Button>
            <button
              onClick={() => { setSoundEnabled(!soundEnabled); toggleSound(!soundEnabled); }}
              className={`h-6 w-6 flex items-center justify-center ${soundEnabled ? 'text-emerald-400' : 'text-white/30'}`}
            >
              {soundEnabled ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}
            </button>
            <div className={`w-1.5 h-1.5 rounded-full ${wsConnected ? 'bg-emerald-500' : 'bg-red-500'}`} />
          </div>
          
          {/* Centre - Navigation avec texte */}
          <nav className="flex items-center gap-1">
            <Button onClick={() => navigate('/')} variant="ghost" size="sm" className="bg-white/20 text-white h-8 px-2 text-xs">
              <Home className="w-4 h-4 mr-1" />
              Caisse
            </Button>
            <Button onClick={() => navigate('/invoices')} variant="ghost" size="sm" className="text-white/70 hover:text-white h-8 px-2 text-xs relative">
              <FileText className="w-4 h-4 mr-1" />
              Factures
              {badgeCounts.quotes > 0 && <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center">{badgeCounts.quotes}</span>}
            </Button>
            <Button onClick={() => navigate('/deposit-receipts')} variant="ghost" size="sm" className="text-white/70 hover:text-white h-8 px-2 text-xs relative">
              <Receipt className="w-4 h-4 mr-1" />
              Bons
              {badgeCounts.deposits > 0 && <span className="absolute -top-1 -right-1 bg-purple-500 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center">{badgeCounts.deposits}</span>}
            </Button>
            <Button onClick={() => navigate('/order-alerts')} variant="ghost" size="sm" className="text-white/70 hover:text-white h-8 px-2 text-xs relative">
              <Package className="w-4 h-4 mr-1" />
              Commandes
              {badgeCounts.orders > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center">{badgeCounts.orders}</span>}
            </Button>
            <Button onClick={() => navigate('/client-notes')} variant="ghost" size="sm" className="text-white/70 hover:text-white h-8 px-2 text-xs">
              <StickyNote className="w-4 h-4 mr-1" />
              Notes
            </Button>
            <Button onClick={() => navigate('/reviews')} variant="ghost" size="sm" className="text-white/70 hover:text-white h-8 px-2 text-xs" data-testid="reviews-nav-button">
              <Star className="w-4 h-4 mr-1" />
              Avis
            </Button>
            <Button onClick={() => navigate('/loyalty')} variant="ghost" size="sm" className="text-white/70 hover:text-white h-8 px-2 text-xs" data-testid="loyalty-nav-button">
              <Gift className="w-4 h-4 mr-1" />
              Fidélité
            </Button>
            <Button onClick={() => navigate('/sav')} variant="ghost" size="sm" className="text-white/70 hover:text-white h-8 px-2 text-xs" data-testid="sav-nav-button">
              <Wrench className="w-4 h-4 mr-1" />
              SAV
            </Button>
            <Button onClick={() => navigate('/product-census')} variant="ghost" size="sm" className="text-white/70 hover:text-white h-8 px-2 text-xs" data-testid="product-census-nav-button">
              <Package className="w-4 h-4 mr-1" />
              Produits
            </Button>
            <Button onClick={() => navigate('/hr-agenda')} variant="ghost" size="sm" className="text-white/70 hover:text-white h-8 px-2 text-xs" data-testid="hr-agenda-nav-button">
              <ClipboardList className="w-4 h-4 mr-1" />
              RH
            </Button>
            <Button onClick={() => setShowDocumentModal(true)} variant="ghost" size="sm" className="text-white/70 hover:text-white h-8 px-2 text-xs" data-testid="quick-doc-button">
              <Upload className="w-4 h-4 mr-1" />
              Doc
            </Button>
            <Button onClick={() => navigate('/history')} variant="ghost" size="sm" className="text-white/70 hover:text-white h-8 px-2 text-xs">
              <History className="w-4 h-4 mr-1" />
              Historique
            </Button>
          </nav>
            
          {/* Droite - Imprimante, Paramètres, Rapports, Déconnexion */}
          <div className="flex items-center gap-0.5">
            <Button onClick={() => setShowPrinterModal(true)} variant="ghost" size="sm" className="text-white/60 h-6 w-6 p-0">
              <Printer className="w-3 h-3" />
            </Button>
            <Button onClick={() => navigate('/settings')} variant="ghost" size="sm" className="text-white/60 h-6 w-6 p-0">
              <Settings className="w-3 h-3" />
            </Button>
            <Button onClick={() => navigate('/reports')} variant="ghost" size="sm" className="text-white/60 h-6 w-6 p-0">
              <TrendingUp className="w-3 h-3" />
            </Button>
            <Button
              onClick={onLogout}
              data-testid="logout-button"
              variant="ghost"
              size="sm"
              className="text-red-500 hover:bg-red-500/20 h-6 w-6 p-0"
            >
              <LogOut className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {/* Mobile Header */}
        <div className="flex md:hidden h-12 items-center justify-between px-3">
          {/* Menu hamburger */}
          <Button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            variant="ghost"
            size="sm"
            className="text-white h-8 w-8 p-0"
          >
            <Menu className="w-5 h-5" />
          </Button>

          {/* Logo / Titre */}
          <div className="flex items-center gap-2">
            <Home className="w-5 h-5 text-emerald-400" />
            <span className="text-white font-bold text-sm">PHONELAB</span>
            {(badgeCounts.orders + badgeCounts.website_quotes + badgeCounts.quote_requests) > 0 && (
              <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                {badgeCounts.orders + badgeCounts.website_quotes + badgeCounts.quote_requests}
              </span>
            )}
          </div>

          {/* Actions rapides mobile */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => { setSoundEnabled(!soundEnabled); toggleSound(!soundEnabled); }}
              className={`h-8 w-8 flex items-center justify-center ${soundEnabled ? 'text-emerald-400' : 'text-white/30'}`}
            >
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            <Button
              onClick={onLogout}
              variant="ghost"
              size="sm"
              className="text-red-500 h-8 w-8 p-0"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-gray-800 border-t border-gray-700 px-2 py-3 space-y-1">
            <div className="grid grid-cols-4 gap-2">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="flex flex-col items-center p-2 rounded-lg bg-emerald-500/20 text-emerald-400">
                <Home className="w-5 h-5 mb-1" />
                <span className="text-[10px]">Caisse</span>
              </button>
              <button onClick={() => { navigate('/invoices'); setMobileMenuOpen(false); }} className="flex flex-col items-center p-2 rounded-lg bg-white/5 text-white/70 relative">
                <FileText className="w-5 h-5 mb-1" />
                <span className="text-[10px]">Factures</span>
                {badgeCounts.quotes > 0 && <span className="absolute top-0 right-1 bg-blue-500 text-white text-[8px] rounded-full w-4 h-4 flex items-center justify-center">{badgeCounts.quotes}</span>}
              </button>
              <button onClick={() => { navigate('/deposit-receipts'); setMobileMenuOpen(false); }} className="flex flex-col items-center p-2 rounded-lg bg-white/5 text-white/70 relative">
                <Receipt className="w-5 h-5 mb-1" />
                <span className="text-[10px]">Bons</span>
                {badgeCounts.deposits > 0 && <span className="absolute top-0 right-1 bg-purple-500 text-white text-[8px] rounded-full w-4 h-4 flex items-center justify-center">{badgeCounts.deposits}</span>}
              </button>
              <button onClick={() => { navigate('/order-alerts'); setMobileMenuOpen(false); }} className="flex flex-col items-center p-2 rounded-lg bg-white/5 text-white/70 relative">
                <Package className="w-5 h-5 mb-1" />
                <span className="text-[10px]">Commandes</span>
                {badgeCounts.orders > 0 && <span className="absolute top-0 right-1 bg-red-500 text-white text-[8px] rounded-full w-4 h-4 flex items-center justify-center">{badgeCounts.orders}</span>}
              </button>
              <button onClick={() => { navigate('/client-notes'); setMobileMenuOpen(false); }} className="flex flex-col items-center p-2 rounded-lg bg-white/5 text-white/70">
                <StickyNote className="w-5 h-5 mb-1" />
                <span className="text-[10px]">Notes</span>
              </button>
              <button onClick={() => { navigate('/reviews'); setMobileMenuOpen(false); }} className="flex flex-col items-center p-2 rounded-lg bg-white/5 text-white/70">
                <Star className="w-5 h-5 mb-1" />
                <span className="text-[10px]">Avis</span>
              </button>
              <button onClick={() => { navigate('/loyalty'); setMobileMenuOpen(false); }} className="flex flex-col items-center p-2 rounded-lg bg-white/5 text-white/70">
                <Gift className="w-5 h-5 mb-1" />
                <span className="text-[10px]">Fidélité</span>
              </button>
              <button onClick={() => { navigate('/sav'); setMobileMenuOpen(false); }} className="flex flex-col items-center p-2 rounded-lg bg-white/5 text-white/70" data-testid="mobile-sav-button">
                <Wrench className="w-5 h-5 mb-1" />
                <span className="text-[10px]">SAV</span>
              </button>
              <button onClick={() => { navigate('/hr-agenda'); setMobileMenuOpen(false); }} className="flex flex-col items-center p-2 rounded-lg bg-white/5 text-white/70">
                <ClipboardList className="w-5 h-5 mb-1" />
                <span className="text-[10px]">RH</span>
              </button>
            </div>
            <div className="flex justify-center gap-4 pt-2 border-t border-gray-700 mt-2">
              <button onClick={() => { navigate('/history'); setMobileMenuOpen(false); }} className="flex items-center gap-1 text-white/60 text-xs">
                <History className="w-4 h-4" /> Historique
              </button>
              <button onClick={() => { navigate('/settings'); setMobileMenuOpen(false); }} className="flex items-center gap-1 text-white/60 text-xs">
                <Settings className="w-4 h-4" /> Paramètres
              </button>
              <button onClick={() => { navigate('/reports'); setMobileMenuOpen(false); }} className="flex items-center gap-1 text-white/60 text-xs">
                <TrendingUp className="w-4 h-4" /> Rapports
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Bandeau d'alerte - Multiples alertes */}
      {(badgeCounts.orders > 0 || badgeCounts.quote_requests > 0 || badgeCounts.diagnostics > 0) && (
        <div 
          className="bg-gradient-to-r from-red-600 via-amber-500 to-red-600 bg-[length:200%_100%] animate-[gradient_2s_ease_infinite] cursor-pointer"
          data-testid="alert-banner"
        >
          <div className="max-w-full mx-auto px-6 py-3 flex items-center justify-center gap-4 flex-wrap">
            <AlertTriangle className="w-5 h-5 text-white animate-bounce" />
            
            {badgeCounts.orders > 0 && (
              <span 
                onClick={() => navigate('/order-alerts')}
                className="text-white font-bold hover:underline cursor-pointer"
              >
                {badgeCounts.orders} commande{badgeCounts.orders > 1 ? 's' : ''} en attente
              </span>
            )}
            
            {badgeCounts.quote_requests > 0 && (
              <span 
                onClick={() => navigate('/invoices?tab=requests')}
                className="text-white font-bold hover:underline cursor-pointer"
              >
                {badgeCounts.quote_requests} demande{badgeCounts.quote_requests > 1 ? 's' : ''} de devis
              </span>
            )}
            
            {badgeCounts.diagnostics > 0 && (
              <span 
                onClick={() => navigate('/invoices?tab=diagnostics')}
                className="text-white font-bold hover:underline cursor-pointer"
              >
                {badgeCounts.diagnostics} diagnostic{badgeCounts.diagnostics > 1 ? 's' : ''} en attente
              </span>
            )}
            
            <AlertTriangle className="w-5 h-5 text-white animate-bounce" />
          </div>
        </div>
      )}

      {/* Main Content - Pleine largeur */}
      <main className="p-3 md:p-6 space-y-4 md:space-y-6 max-w-full mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <h2 className="text-lg md:text-2xl font-semibold text-white mb-1 flex flex-wrap items-center gap-2 md:gap-3">
                Encaissements du jour
                {totalReopens > 0 && (
                  <span className="inline-flex items-center px-2 py-0.5 md:px-3 md:py-1 rounded-full text-[10px] md:text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/30" data-testid="reopen-badge">
                    Caisse réouverte {totalReopens} fois (total)
                  </span>
                )}
              </h2>
              <Input
                type="date"
                data-testid="date-picker"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-auto bg-gray-800 border-gray-600 text-white focus:border-white/50 [color-scheme:dark]"
              />
            </div>
            <div className="flex gap-2 md:gap-3">
              {!isCashRegisterClosed && (
                <Button
                  onClick={() => setShowExpenseModal(true)}
                  data-testid="add-expense-button"
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold h-10 md:h-12 px-3 md:px-6 text-sm md:text-base"
                >
                  <Minus className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" />
                  <span className="hidden sm:inline">Sortie de caisse</span>
                  <span className="sm:hidden">Sortie</span>
                </Button>
              )}
              {isCashRegisterClosed ? (
              <Button
                onClick={handleReopenCashRegister}
                data-testid="reopen-cash-register-button"
                className="bg-amber-600 hover:bg-amber-700 text-white font-semibold text-sm md:text-base"
              >
                Réouvrir la caisse
              </Button>
            ) : entries.length > 0 ? (
              <div className="flex flex-col items-end gap-1">
                <Button
                  onClick={handleCloseCashRegister}
                  data-testid="close-cash-register-button"
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold text-sm md:text-base"
                >
                  Fermer la caisse
                </Button>
                {entries.some(e => e.status !== 'validated') && (
                  <span className="text-xs text-orange-300 font-medium" data-testid="validation-warning">
                    ⚠️ {entries.filter(e => e.status !== 'validated').length} ligne(s) non validée(s)
                  </span>
                )}
              </div>
            ) : null}
          </div>
        </div>

        {showSummary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="p-1.5 md:p-2 rounded-lg bg-blue-500/20">
                    <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-blue-300" />
                  </div>
                  <div>
                    <p className="text-[10px] md:text-xs text-white/60 uppercase tracking-wide">Total Encaissements</p>
                    <p className="text-base md:text-xl font-bold text-white">{formatCurrency(summary.total_revenue)}</p>
                  </div>
                </div>
                <InfoTooltip 
                  title="Total Encaissements"
                  description="Somme de toutes les ventes du jour, tous modes de paiement confondus."
                  formula="Espèces + Carte + Mixte"
                />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="p-1.5 md:p-2 rounded-lg bg-emerald-500/20">
                    <Banknote className="w-4 h-4 md:w-5 md:h-5 text-emerald-300" />
                  </div>
                  <div>
                    <p className="text-[10px] md:text-xs text-white/60 uppercase tracking-wide">Espèces ({summary.cash_count})</p>
                    <p className="text-base md:text-xl font-bold text-emerald-300">{formatCurrency(summary.total_cash)}</p>
                  </div>
                </div>
                <InfoTooltip 
                  title="Total Espèces"
                  description="Somme des paiements reçus en espèces aujourd'hui. Ce montant devrait correspondre à votre caisse physique."
                  formula="Σ Ventes payées en espèces"
                />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="p-1.5 md:p-2 rounded-lg bg-sky-500/20">
                    <CreditCard className="w-4 h-4 md:w-5 md:h-5 text-sky-300" />
                  </div>
                  <div>
                    <p className="text-[10px] md:text-xs text-white/60 uppercase tracking-wide">Carte ({summary.card_count})</p>
                    <p className="text-base md:text-xl font-bold text-sky-300">{formatCurrency(summary.total_card)}</p>
                  </div>
                </div>
                <InfoTooltip 
                  title="Total Carte Bancaire"
                  description="Somme des paiements reçus par carte (CB, Visa, Mastercard). Ce montant sera viré sur votre compte bancaire."
                  formula="Σ Ventes payées par carte"
                />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="p-1.5 md:p-2 rounded-lg bg-purple-500/20">
                    <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-purple-300" />
                  </div>
                  <div>
                    <p className="text-[10px] md:text-xs text-white/60 uppercase tracking-wide">Marge Totale</p>
                    <p className="text-base md:text-xl font-bold text-purple-300">{formatCurrency(summary.total_margin)}</p>
                  </div>
                </div>
                <InfoTooltip 
                  title="Marge Totale (Bénéfice brut)"
                  description="Votre bénéfice du jour = différence entre prix de vente et prix d'achat de chaque article."
                  formula="Σ (Prix vente - Prix achat) × Quantité"
                />
              </div>
            </div>
          </div>
        )}

        {/* Section Sorties de caisse - Toujours visible */}
        <div className="rounded-xl border border-red-500/50 bg-red-900/30 shadow-lg overflow-hidden" data-testid="expenses-section">
          <div className="p-3 md:p-4 border-b border-red-500/30 bg-red-900/50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-white flex items-center gap-2 text-sm md:text-lg">
                <Minus className="w-4 h-4 md:w-5 md:h-5 text-red-400" />
                <span className="hidden sm:inline">Sorties de caisse (Remboursements, Frais, Fournisseurs)</span>
                <span className="sm:hidden">Sorties de caisse</span>
              </h3>
              <InfoTooltip 
                title="Sorties de caisse"
                description="Dépenses effectuées depuis la caisse : remboursements clients, achats fournisseurs en espèces, petits frais divers."
                formula="Ces montants sont soustraits de votre caisse espèces"
              />
            </div>
            {!isCashRegisterClosed && (
              <Button
                onClick={() => setShowExpenseModal(true)}
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Plus className="w-4 h-4 mr-1" />
                Ajouter
              </Button>
            )}
          </div>
          {expenses.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-base">
                <thead className="bg-red-900/40 border-b border-red-500/30 text-white uppercase text-sm font-semibold">
                  <tr>
                    <th className="px-4 py-3 text-left">Description</th>
                    <th className="px-4 py-3 text-right">Montant</th>
                    <th className="px-4 py-3 text-center">Paiement</th>
                    <th className="px-4 py-3 text-center">Catégorie</th>
                    <th className="px-4 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-red-500/20">
                  {expenses.map((expense) => (
                    <tr key={expense.id} className="hover:bg-red-900/30 transition-colors" data-testid="expense-row">
                      <td className="px-4 py-3 text-white text-lg font-medium">{expense.description}</td>
                      <td className="px-4 py-3 text-right font-mono font-bold text-red-400 text-xl">
                        -{formatCurrency(expense.amount)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${
                          expense.payment_method === 'cash'
                            ? 'bg-emerald-500 text-white'
                            : 'bg-sky-500 text-white'
                        }`}>
                          {expense.payment_method === 'cash' ? (
                            <><Banknote className="w-4 h-4 mr-1" /> Espèce</>
                          ) : (
                            <><CreditCard className="w-4 h-4 mr-1" /> CB</>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-gray-700 text-white">
                          {expense.category === 'refund' && '↩️ Remboursement'}
                          {expense.category === 'supplier' && '📦 Fournisseur'}
                          {expense.category === 'expense' && '💰 Frais'}
                          {expense.category === 'salary' && '👤 Salaire'}
                          {expense.category === 'other' && '📝 Autre'}
                          {!['refund', 'supplier', 'expense', 'salary', 'other'].includes(expense.category) && expense.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button
                          onClick={() => handleDeleteExpense(expense.id)}
                          data-testid="delete-expense-button"
                          size="sm"
                          variant="ghost"
                          className="hover:bg-red-500/30 text-white hover:text-red-300 h-10"
                          disabled={isCashRegisterClosed}
                        >
                          <Trash2 className="w-5 h-5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-red-900/40 border-t border-red-500/30">
                  <tr>
                    <td className="px-4 py-3 text-white font-bold text-lg">Total Sorties</td>
                    <td className="px-4 py-3 text-right font-mono font-bold text-red-400 text-xl">
                      -{formatCurrency(expenses.reduce((sum, e) => sum + e.amount, 0))}
                    </td>
                    <td colSpan="3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-white/50">
              <Minus className="w-12 h-12 mx-auto mb-3 text-red-400/50" />
              <p className="text-lg">Aucune sortie de caisse aujourd'hui</p>
              <p className="text-sm mt-1">Cliquez sur "Ajouter" pour enregistrer un remboursement, frais ou paiement fournisseur</p>
            </div>
          )}
        </div>

        {/* Onglets Entrées / Annulations */}
        <div className="flex gap-2 mb-4" data-testid="entries-tabs">
          <Button
            onClick={() => setActiveTab('entries')}
            variant={activeTab === 'entries' ? 'default' : 'outline'}
            className={activeTab === 'entries' 
              ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
              : 'bg-gray-800 text-white/70 border-gray-600 hover:bg-gray-700'}
          >
            Entrées du jour
            {entries.length > 0 && (
              <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs">{entries.length}</span>
            )}
          </Button>
          <Button
            onClick={() => setActiveTab('cancelled')}
            variant={activeTab === 'cancelled' ? 'default' : 'outline'}
            className={activeTab === 'cancelled' 
              ? 'bg-red-600 hover:bg-red-700 text-white' 
              : 'bg-gray-800 text-white/70 border-gray-600 hover:bg-gray-700'}
          >
            Annulations
            {cancelledEntries.length > 0 && (
              <span className="ml-2 bg-red-500/50 px-2 py-0.5 rounded-full text-xs">{cancelledEntries.length}</span>
            )}
          </Button>
        </div>

        {/* Tableau des Entrées */}
        {activeTab === 'entries' && (
        <div className="rounded-xl border border-gray-600 shadow-lg bg-gray-900 overflow-hidden" data-testid="entries-table-container">
          <div className="overflow-x-auto">
            <table className="w-full text-base text-left">
              <thead className="bg-gray-800 border-b border-gray-600 text-white uppercase text-sm font-semibold">
                <tr>
                  <th className="px-2 py-4 w-16">Heure</th>
                  <th className="px-2 py-4" style={{minWidth: '280px'}}>Description</th>
                  <th className="px-2 py-4 text-center w-24">Qté</th>
                  <th className="px-2 py-4 text-center w-28">P.Achat</th>
                  <th className="px-2 py-4 text-center w-28">P.Vente</th>
                  <th className="px-2 py-4 text-center w-28">Total</th>
                  <th className="px-2 py-4 text-center w-28">Paiement</th>
                  <th className="px-2 py-4 text-center w-28">Marge</th>
                  <th className="px-2 py-4 text-center w-20">Fidélité</th>
                  <th className="px-2 py-4 text-center w-16">Statut</th>
                  <th className="px-2 py-4 text-center w-36">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-600/50">
                {/* Ligne de saisie - bloquée si caisse fermée */}
                {isCashRegisterClosed ? (
                  <tr className="bg-gray-700/50 border-b-2 border-gray-600" data-testid="locked-entry-row">
                    <td colSpan="10" className="px-6 py-8 text-center">
                      <div className="flex flex-col items-center justify-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
                          <X className="w-8 h-8 text-red-400" />
                        </div>
                        <div>
                          <p className="text-xl font-bold text-white mb-2">Caisse fermée</p>
                          <p className="text-white/60 mb-4">Vous devez réouvrir la caisse pour ajouter des entrées</p>
                          <Button
                            onClick={handleReopenCashRegister}
                            className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-6 py-3"
                            data-testid="reopen-inline-button"
                          >
                            🔓 Réouvrir la caisse pour continuer
                          </Button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr className="bg-emerald-600/40 border-b-2 border-emerald-400" data-testid="new-entry-row">
                    <td className="px-2 py-3 text-center text-white/60 text-sm">
                      --:--
                    </td>
                    <td className="px-2 py-3">
                      <input
                        ref={(el) => {
                          if (!window.descriptionInputRef) window.descriptionInputRef = el;
                        }}
                        value={newEntry.description}
                        id="description-input"
                        data-testid="description-input"
                        onChange={(e) => setNewEntry({ ...newEntry, description: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            // Si tout est rempli et mode de paiement sélectionné, soumettre
                            if (newEntry.description && newEntry.sale_price && newEntry.payment_method) {
                              handleAddEntry(e);
                            } else if (newEntry.description && newEntry.sale_price && !newEntry.payment_method) {
                              // Si pas de mode de paiement, ouvrir modal
                              openPaymentModal();
                            } else {
                              // Sinon, passer au champ suivant
                              document.getElementById('quantity-input')?.focus();
                            }
                          }
                        }}
                        placeholder="Nom du produit ou service..."
                        className="w-full h-16 px-4 text-2xl font-bold rounded-lg bg-white/20 border-2 border-white/40 text-white placeholder:text-white/60 focus:outline-none focus:border-emerald-400 focus:bg-white/30"
                      />
                    </td>
                    <td className="px-2 py-3">
                      <input
                        type="number"
                        id="quantity-input"
                        data-testid="quantity-input"
                        value={newEntry.quantity}
                        onChange={(e) => setNewEntry({ ...newEntry, quantity: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            document.getElementById('purchase-price-input')?.focus();
                          }
                        }}
                        placeholder="Qté"
                        className="w-full h-16 px-2 text-2xl text-center font-bold rounded-lg bg-yellow-500/40 border-2 border-yellow-400 text-white placeholder:text-yellow-200/70 focus:outline-none focus:border-yellow-300 focus:bg-yellow-500/50"
                      />
                    </td>
                    <td className="px-2 py-3">
                      <input
                        type="number"
                        id="purchase-price-input"
                        data-testid="purchase-price-input"
                        value={newEntry.purchase_price}
                        onChange={(e) => setNewEntry({ ...newEntry, purchase_price: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            document.getElementById('sale-price-input')?.focus();
                          }
                        }}
                        placeholder="Achat"
                        className="w-full h-16 px-2 text-2xl text-center font-bold rounded-lg bg-orange-500/40 border-2 border-orange-400 text-white placeholder:text-orange-200/70 focus:outline-none focus:border-orange-300 focus:bg-orange-500/50"
                      />
                    </td>
                    <td className="px-2 py-3">
                      <input
                        type="number"
                        id="sale-price-input"
                        data-testid="sale-price-input"
                        value={newEntry.sale_price}
                        onChange={(e) => setNewEntry({ ...newEntry, sale_price: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            // Si mode de paiement déjà sélectionné, soumettre directement
                            if (newEntry.payment_method) {
                              handleAddEntry(e);
                            } else {
                              // Sinon ouvrir le modal de paiement
                              openPaymentModal();
                            }
                          }
                        }}
                        placeholder="Vente"
                        required
                      className="w-full h-16 px-2 text-2xl text-center font-bold rounded-lg bg-emerald-500/40 border-2 border-emerald-400 text-white placeholder:text-emerald-200/70 focus:outline-none focus:border-emerald-300 focus:bg-emerald-500/50"
                    />
                  </td>
                  <td className="px-2 py-3 text-center font-mono text-white text-2xl font-bold">
                    {newEntry.quantity && newEntry.sale_price
                      ? formatCurrency(parseFloat(newEntry.quantity) * parseFloat(newEntry.sale_price))
                      : '0,00 €'}
                  </td>
                  <td className="px-2 py-3 text-center">
                    <button
                      type="button"
                      id="payment-button"
                      data-testid="payment-button"
                      onClick={openPaymentModal}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1 mx-auto ${
                        newEntry.payment_method
                          ? newEntry.payment_method === 'cash'
                            ? 'bg-emerald-500 text-white border-2 border-emerald-300'
                            : newEntry.payment_method === 'card'
                              ? 'bg-sky-500 text-white border-2 border-sky-300'
                              : newEntry.payment_method === 'mixed'
                                ? 'bg-purple-500 text-white border-2 border-purple-300'
                                : 'bg-amber-500 text-white border-2 border-amber-300'
                          : 'bg-orange-500/60 text-white border-2 border-orange-400 animate-pulse'
                      }`}
                    >
                      {newEntry.payment_method === 'cash' && <><Banknote className="w-3 h-3" /> Espèces</>}
                      {newEntry.payment_method === 'card' && <><CreditCard className="w-3 h-3" /> CB</>}
                      {newEntry.payment_method === 'mixed' && <><Split className="w-3 h-3" /> Mixte</>}
                      {newEntry.payment_method === 'installment' && <><Clock className="w-3 h-3" /> {newEntry.installment_count}x</>}
                      {!newEntry.payment_method && <><Wallet className="w-3 h-3" /> Paiement</>}
                    </button>
                  </td>
                  <td className="px-3 py-4 text-right font-mono text-white text-lg">
                    {newEntry.quantity && newEntry.sale_price && newEntry.purchase_price
                      ? formatCurrency(
                          (parseFloat(newEntry.sale_price) - parseFloat(newEntry.purchase_price)) *
                            parseFloat(newEntry.quantity)
                        )
                      : '0,00 €'}
                  </td>
                  <td className="px-3 py-4 text-center">
                    {/* Empty cell for Status column alignment */}
                  </td>
                  <td className="px-3 py-4 text-center">
                    <Button
                      onClick={handleAddEntry}
                      id="add-entry-button"
                      data-testid="add-entry-button"
                      size="sm"
                      className="bg-emerald-500 hover:bg-emerald-600 text-white h-14 px-6 text-base font-bold"
                    >
                      <Plus className="w-6 h-6" />
                    </Button>
                  </td>
                </tr>
                )}

                {[...entries].reverse().map((entry) => {
                  const isHighlighted = nonValidatedHighlight.includes(entry.id);
                  const isNotValidated = entry.status !== 'validated';
                  const isEditing = editingEntry === entry.id;
                  const isAutoEditable = isNotValidated && !isEditing;
                  
                  return (
                  <tr 
                    key={entry.id} 
                    className={`
                      hover:bg-slate-600/50 transition-colors bg-slate-700/50
                      ${entry.is_modified ? 'bg-amber-500/20 border-l-4 border-amber-400' : ''} 
                      ${isHighlighted ? 'bg-red-500/30 border-l-4 border-red-500 animate-pulse' : ''}
                      ${isNotValidated && !isHighlighted ? 'border-l-4 border-orange-400 bg-orange-500/20' : ''}
                    `} 
                    data-testid="entry-row"
                  >
                    <td className="px-4 py-4 text-sm text-white font-mono">
                      {isNotValidated && (
                        <span className="inline-block w-2 h-2 bg-orange-500 rounded-full animate-ping mr-2"></span>
                      )}
                      {formatTime(entry.created_at)}
                    </td>
                    <td className="px-4 py-4 text-white text-lg font-semibold">
                      {isEditing || isAutoEditable ? (
                        <Input
                          value={isEditing ? editForm.description : entry.description}
                          onChange={(e) => {
                            if (isEditing) {
                              setEditForm({ ...editForm, description: e.target.value });
                            } else {
                              // Auto-edit mode
                              setEditingEntry(entry.id);
                              setEditForm({
                                description: e.target.value,
                                quantity: entry.quantity,
                                purchase_price: entry.purchase_price,
                                sale_price: entry.sale_price,
                                payment_method: entry.payment_method
                              });
                            }
                          }}
                          className="h-12 text-lg border-orange-400 bg-white/20 text-white font-semibold"
                          data-testid="edit-description-input"
                        />
                      ) : (
                        <div className="flex items-center gap-2">
                          {entry.description}
                          {entry.is_modified && (
                            <button
                              onClick={() => viewHistory(entry.id, entry.description)}
                              className="text-amber-400 hover:text-amber-300 transition-colors flex items-center gap-1"
                              title="Voir l'historique des modifications"
                              data-testid="view-history-button"
                            >
                              <FileText className="w-4 h-4" />
                              <span className="text-xs bg-amber-500/30 px-1.5 py-0.5 rounded">
                                {entry.history?.length || 1} modif.
                              </span>
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-center font-mono text-white text-lg">
                      {isEditing || isAutoEditable ? (
                        <Input
                          type="number"
                          value={isEditing ? editForm.quantity : entry.quantity}
                          onChange={(e) => {
                            if (isEditing) {
                              setEditForm({ ...editForm, quantity: parseInt(e.target.value) });
                            } else {
                              setEditingEntry(entry.id);
                              setEditForm({
                                description: entry.description,
                                quantity: parseInt(e.target.value),
                                purchase_price: entry.purchase_price,
                                sale_price: entry.sale_price,
                                payment_method: entry.payment_method
                              });
                            }
                          }}
                          className="h-12 text-lg text-center border-orange-400 bg-white/20 text-white"
                          data-testid="edit-quantity-input"
                        />
                      ) : (
                        entry.quantity
                      )}
                    </td>
                    <td className="px-4 py-4 text-right font-mono text-white text-lg">
                      {isEditing || isAutoEditable ? (
                        <Input
                          type="number"
                          value={isEditing ? editForm.purchase_price : entry.purchase_price}
                          onChange={(e) => {
                            if (isEditing) {
                              setEditForm({ ...editForm, purchase_price: parseFloat(e.target.value) });
                            } else {
                              setEditingEntry(entry.id);
                              setEditForm({
                                description: entry.description,
                                quantity: entry.quantity,
                                purchase_price: parseFloat(e.target.value),
                                sale_price: entry.sale_price,
                                payment_method: entry.payment_method
                              });
                            }
                          }}
                          className="h-12 text-lg text-right border-orange-400 bg-white/20 text-white"
                          data-testid="edit-purchase-price-input"
                        />
                      ) : (
                        formatCurrency(entry.purchase_price)
                      )}
                    </td>
                    <td className="px-4 py-4 text-right font-mono text-white text-lg">
                      {isEditing || isAutoEditable ? (
                        <Input
                          type="number"
                          value={isEditing ? editForm.sale_price : entry.sale_price}
                          onChange={(e) => {
                            if (isEditing) {
                              setEditForm({ ...editForm, sale_price: parseFloat(e.target.value) });
                            } else {
                              setEditingEntry(entry.id);
                              setEditForm({
                                description: entry.description,
                                quantity: entry.quantity,
                                purchase_price: entry.purchase_price,
                                sale_price: parseFloat(e.target.value),
                                payment_method: entry.payment_method
                              });
                            }
                          }}
                          className="h-12 text-lg text-right border-orange-400 bg-white/20 text-white"
                          data-testid="edit-sale-price-input"
                        />
                      ) : (
                        formatCurrency(entry.sale_price)
                      )}
                    </td>
                    <td className="px-4 py-4 text-right font-mono font-bold text-yellow-300 text-xl">
                      {editingEntry === entry.id
                        ? formatCurrency((editForm.sale_price || 0) * (editForm.quantity || 0))
                        : formatCurrency(entry.sale_price * entry.quantity)}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {editingEntry === entry.id ? (
                        <div className="flex gap-2 justify-center flex-wrap">
                          <button
                            type="button"
                            onClick={() => setEditForm({ ...editForm, payment_method: 'cash' })}
                            className={`px-3 py-2 rounded-full text-xs font-bold transition-all ${
                              editForm.payment_method === 'cash'
                                ? 'bg-emerald-500 text-white border-2 border-emerald-300'
                                : 'bg-white/20 text-white border-2 border-transparent hover:bg-white/30'
                            }`}
                            data-testid="edit-payment-cash-button"
                          >
                            <Banknote className="w-3 h-3 inline mr-1" />
                            Espèce
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditForm({ ...editForm, payment_method: 'card' })}
                            className={`px-3 py-2 rounded-full text-xs font-bold transition-all ${
                              editForm.payment_method === 'card'
                                ? 'bg-sky-500 text-white border-2 border-sky-300'
                                : 'bg-white/20 text-white border-2 border-transparent hover:bg-white/30'
                            }`}
                            data-testid="edit-payment-card-button"
                          >
                            <CreditCard className="w-3 h-3 inline mr-1" />
                            CB
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditForm({ ...editForm, payment_method: 'mixed' })}
                            className={`px-3 py-2 rounded-full text-xs font-bold transition-all ${
                              editForm.payment_method === 'mixed'
                                ? 'bg-purple-500 text-white border-2 border-purple-300'
                                : 'bg-white/20 text-white border-2 border-transparent hover:bg-white/30'
                            }`}
                            data-testid="edit-payment-mixed-button"
                          >
                            <Split className="w-3 h-3 inline mr-1" />
                            Mixte
                          </button>
                        </div>
                      ) : (
                        <span
                          className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-bold ${
                            entry.payment_method === 'cash'
                              ? 'bg-emerald-500 text-white'
                              : entry.payment_method === 'card'
                                ? 'bg-sky-500 text-white'
                                : entry.payment_method === 'mixed'
                                  ? 'bg-purple-500 text-white'
                                  : entry.payment_method === 'installment'
                                    ? 'bg-amber-500 text-white'
                                    : 'bg-gray-500 text-white'
                          }`}
                          data-testid="payment-badge"
                          title={
                            entry.payment_method === 'mixed' && entry.payment_details
                              ? `Espèces: ${formatCurrency(entry.payment_details.cash_amount || 0)} / CB: ${formatCurrency(entry.payment_details.card_amount || 0)}`
                              : entry.payment_method === 'installment' && entry.payment_details
                                ? `${entry.payment_details.installment_paid}/${entry.payment_details.installment_count} versements payés`
                                : ''
                          }
                        >
                          {entry.payment_method === 'cash' && (
                            <>
                              <Banknote className="w-4 h-4 mr-1" /> Espèce
                            </>
                          )}
                          {entry.payment_method === 'card' && (
                            <>
                              <CreditCard className="w-4 h-4 mr-1" /> CB
                            </>
                          )}
                          {entry.payment_method === 'mixed' && (
                            <>
                              <Split className="w-4 h-4 mr-1" /> Mixte
                            </>
                          )}
                          {entry.payment_method === 'installment' && (
                            <>
                              <Clock className="w-4 h-4 mr-1" /> {entry.payment_details?.installment_paid || 1}/{entry.payment_details?.installment_count || 2}x
                            </>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right font-mono text-emerald-300 text-lg font-semibold">
                      {editingEntry === entry.id
                        ? formatCurrency(((editForm.sale_price || 0) - (editForm.purchase_price || 0)) * (editForm.quantity || 0))
                        : formatCurrency(entry.margin)}
                    </td>
                    {/* Colonne Fidélité - Icône petit bonhomme */}
                    <td className="px-2 py-4 text-center">
                      {entryLoyaltyMap[entry.id] ? (
                        // Fidélité déjà attribuée
                        <button
                          onClick={() => openLoyaltyModal(entry.id)}
                          className={`relative inline-flex items-center justify-center w-10 h-10 rounded-full transition-all ${
                            entryLoyaltyMap[entry.id].mode === 'none'
                              ? 'bg-gray-500/30 text-gray-400'
                              : 'bg-green-500/30 text-green-400 hover:bg-green-500/40'
                          }`}
                          title={
                            entryLoyaltyMap[entry.id].mode === 'none'
                              ? 'Pas de compte fidélité'
                              : `${entryLoyaltyMap[entry.id].client?.name} (+${entryPointsPreview[entry.id] || 0} pts)`
                          }
                          disabled={isCashRegisterClosed}
                        >
                          {entryLoyaltyMap[entry.id].mode === 'none' ? (
                            <User className="w-5 h-5" />
                          ) : (
                            <>
                              <UserCheck className="w-5 h-5" />
                              <span className="absolute -top-1 -right-1 bg-green-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                +{entryPointsPreview[entry.id] || 0}
                              </span>
                            </>
                          )}
                        </button>
                      ) : (
                        // Fidélité non attribuée - bouton OPTIONNEL (pas de clignotant)
                        <button
                          onClick={() => openLoyaltyModal(entry.id)}
                          className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-slate-500/30 text-slate-400 hover:bg-slate-500/50 transition-all"
                          title="Attribuer les points fidélité (optionnel)"
                          disabled={isCashRegisterClosed}
                          data-testid={`loyalty-btn-${entry.id}`}
                        >
                          <User className="w-5 h-5" />
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {/* Bouton validation - FIDÉLITÉ OPTIONNELLE */}
                      <Button
                        onClick={() => handleValidateAndNext(entry.id, entry.status)}
                        data-testid="validate-button"
                        size="sm"
                        variant={entry.status === 'validated' ? 'default' : 'outline'}
                        className={`h-10 px-3 ${
                          entry.status === 'validated'
                            ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                            : isHighlighted 
                              ? 'border-red-500 bg-red-500/30 text-white hover:bg-red-500/40'
                              : 'border-green-400 bg-green-500/30 text-white hover:bg-green-500/40'
                        }`}
                        disabled={isCashRegisterClosed}
                      >
                        <Check className="w-5 h-5" />
                      </Button>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {editingEntry === entry.id ? (
                          <>
                            <Button
                              onClick={() => saveEdit(entry.id)}
                              data-testid="save-edit-button"
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white h-10"
                            >
                              <Save className="w-5 h-5" />
                            </Button>
                            <Button
                              onClick={cancelEdit}
                              data-testid="cancel-edit-button"
                              size="sm"
                              variant="ghost"
                              className="hover:bg-white/20 text-white h-10"
                            >
                              <X className="w-5 h-5" />
                            </Button>
                          </>
                        ) : (
                          <>
                            {/* Ticket de caisse button */}
                            <Button
                              onClick={() => printTicket(entry)}
                              data-testid="print-ticket-button"
                              size="sm"
                              variant="ghost"
                              className="hover:bg-white/20 text-white h-10"
                              title="Ticket de caisse"
                            >
                              <Receipt className="w-5 h-5" />
                            </Button>
                            {/* Créer facture button */}
                            <Button
                              onClick={() => navigate(`/invoices/new?description=${encodeURIComponent(entry.description)}&amount=${entry.sale_price * entry.quantity}`)}
                              data-testid="create-invoice-button"
                              size="sm"
                              variant="ghost"
                              className="hover:bg-white/20 text-white h-10"
                              title="Créer une facture"
                            >
                              <FileText className="w-5 h-5" />
                            </Button>
                            <Button
                              onClick={() => startEdit(entry)}
                              data-testid="edit-entry-button"
                              size="sm"
                              variant="ghost"
                              className="hover:bg-white/20 text-white h-10"
                              disabled={isCashRegisterClosed}
                            >
                              <Edit2 className="w-5 h-5" />
                            </Button>
                            <Button
                              onClick={() => handleDeleteEntry(entry.id)}
                              data-testid="delete-entry-button"
                              size="sm"
                              variant="ghost"
                              className="hover:bg-red-500/30 text-white hover:text-red-300 h-10"
                              disabled={isCashRegisterClosed}
                            >
                              <Trash2 className="w-5 h-5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}

                {entries.length === 0 && (
                  <tr>
                    <td colSpan="10" className="px-4 py-12 text-center text-white/60 text-lg">
                      Aucun encaissement pour cette date
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Tableau des Annulations */}
        {activeTab === 'cancelled' && (
          <div className="rounded-xl border border-red-600/50 shadow-lg bg-gray-900 overflow-hidden" data-testid="cancelled-table-container">
            <div className="overflow-x-auto">
              <table className="w-full text-base text-left">
                <thead className="bg-red-900/40 border-b border-red-500/30 text-white uppercase text-sm font-semibold">
                  <tr>
                    <th className="px-4 py-4">Date origine</th>
                    <th className="px-4 py-4">Description</th>
                    <th className="px-4 py-4 text-center">Montant</th>
                    <th className="px-4 py-4 text-center">Paiement</th>
                    <th className="px-4 py-4">Motif d'annulation</th>
                    <th className="px-4 py-4 text-center">Annulé le</th>
                  </tr>
                </thead>
                <tbody>
                  {cancelledEntries.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-12 text-center text-white/50">
                        <X className="w-12 h-12 mx-auto mb-3 text-red-400/30" />
                        <p className="text-lg">Aucune annulation pour cette date</p>
                        <p className="text-sm mt-1">Les lignes annulées avec motif apparaîtront ici</p>
                      </td>
                    </tr>
                  ) : (
                    cancelledEntries.map((entry) => (
                      <tr key={entry.id} className="border-b border-gray-700/50 hover:bg-red-900/20">
                        <td className="px-4 py-3 text-white/80">{entry.original_date}</td>
                        <td className="px-4 py-3 text-white font-medium">{entry.description}</td>
                        <td className="px-4 py-3 text-center text-red-400 font-mono font-bold">
                          {formatCurrency(entry.total_amount || (entry.sale_price * entry.quantity))}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            entry.payment_method === 'cash' ? 'bg-green-500/20 text-green-400' :
                            entry.payment_method === 'card' ? 'bg-blue-500/20 text-blue-400' :
                            entry.payment_method === 'mixed' ? 'bg-purple-500/20 text-purple-400' :
                            'bg-orange-500/20 text-orange-400'
                          }`}>
                            {entry.payment_method === 'cash' ? 'Espèces' :
                             entry.payment_method === 'card' ? 'CB' :
                             entry.payment_method === 'mixed' ? 'Mixte' :
                             entry.payment_method === 'installment' ? 'Échelonné' : entry.payment_method}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-yellow-400 italic">"{entry.reason}"</td>
                        <td className="px-4 py-3 text-center text-white/60 text-sm">
                          {entry.cancelled_at ? new Date(entry.cancelled_at).toLocaleString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {cancelledEntries.length > 0 && (
                  <tfoot className="bg-red-900/30 border-t border-red-500/30">
                    <tr>
                      <td className="px-4 py-3 text-white font-bold" colSpan="2">Total Annulé</td>
                      <td className="px-4 py-3 text-center font-mono font-bold text-red-400">
                        {formatCurrency(cancelledEntries.reduce((sum, e) => sum + (e.total_amount || (e.sale_price * e.quantity)), 0))}
                      </td>
                      <td colSpan="3"></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}
      </main>

      <ModificationHistoryModal
        isOpen={historyModal.isOpen}
        onClose={() => setHistoryModal({ isOpen: false, entryId: null, history: [], description: '' })}
        history={historyModal.history}
        entryDescription={historyModal.description}
      />

      <ExpenseModal
        isOpen={showExpenseModal}
        onClose={() => setShowExpenseModal(false)}
        onAddExpense={handleAddExpense}
      />

      <TwoFactorAuthModal
        isOpen={showMondayAuth}
        onClose={() => {}} // Ne pas permettre de fermer sans s'authentifier
        onAuthenticated={handleMondayAuthSuccess}
        purpose="dashboard_monday"
        requireBoth={false}
        emailOnly={true}
        title="Authentification Hebdomadaire"
      />

      <AdminSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />

      {/* Modal Configuration Imprimante POS */}
      {showPrinterModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6 border border-white/20 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Printer className="w-5 h-5" />
                Configuration Imprimante POS
              </h2>
              <button 
                onClick={() => setShowPrinterModal(false)} 
                className="text-white/40 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Section QZ Tray */}
            <div className="mb-6 p-4 rounded-lg bg-slate-700/50 border border-white/10">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {qzStatus.connected ? (
                    <Wifi className="w-5 h-5 text-emerald-400" />
                  ) : (
                    <WifiOff className="w-5 h-5 text-red-400" />
                  )}
                  <span className="font-medium text-white">QZ Tray</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${qzStatus.connected ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>
                    {qzStatus.connected ? 'Connecté' : 'Non connecté'}
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={refreshPrinters}
                  className="text-xs border-white/20 text-white hover:bg-white/10"
                >
                  Actualiser
                </Button>
              </div>
              
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="checkbox"
                  id="useQzTray"
                  checked={printerConfig.useQzTray}
                  onChange={(e) => setPrinterConfig({...printerConfig, useQzTray: e.target.checked})}
                  className="w-4 h-4 rounded bg-white/10 border-white/20"
                />
                <label htmlFor="useQzTray" className="text-sm text-white/80">
                  Activer l'impression directe (sans dialogue)
                </label>
              </div>
              
              {printerConfig.useQzTray && (
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1">Imprimante POS</label>
                  <select
                    value={printerConfig.printerName}
                    onChange={(e) => setPrinterConfig({...printerConfig, printerName: e.target.value})}
                    className="w-full h-10 px-3 rounded-md bg-white/10 border border-white/20 text-white"
                  >
                    <option value="" className="bg-slate-800">Auto-détection</option>
                    {availablePrinters.map((printer, idx) => (
                      <option key={idx} value={printer} className="bg-slate-800">{printer}</option>
                    ))}
                  </select>
                  {availablePrinters.length === 0 && qzStatus.connected && (
                    <p className="text-xs text-amber-400 mt-1">Aucune imprimante détectée</p>
                  )}
                </div>
              )}
              
              {!qzStatus.available && (
                <div className="mt-3 p-3 rounded bg-amber-500/10 border border-amber-500/30">
                  <p className="text-xs text-amber-300">
                    <strong>QZ Tray non installé.</strong><br/>
                    Téléchargez-le gratuitement sur <a href="https://qz.io/download" target="_blank" rel="noopener noreferrer" className="underline">qz.io/download</a> pour activer l'impression automatique.
                  </p>
                </div>
              )}
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Nom du magasin</label>
                <input
                  type="text"
                  value={printerConfig.shopName}
                  onChange={(e) => setPrinterConfig({...printerConfig, shopName: e.target.value})}
                  className="w-full h-10 px-3 rounded-md bg-white/10 border border-white/20 text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Adresse</label>
                <input
                  type="text"
                  value={printerConfig.address}
                  onChange={(e) => setPrinterConfig({...printerConfig, address: e.target.value})}
                  className="w-full h-10 px-3 rounded-md bg-white/10 border border-white/20 text-white"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1">Ville / CP</label>
                  <input
                    type="text"
                    value={printerConfig.city}
                    onChange={(e) => setPrinterConfig({...printerConfig, city: e.target.value})}
                    className="w-full h-10 px-3 rounded-md bg-white/10 border border-white/20 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1">Téléphone</label>
                  <input
                    type="text"
                    value={printerConfig.phone}
                    onChange={(e) => setPrinterConfig({...printerConfig, phone: e.target.value})}
                    className="w-full h-10 px-3 rounded-md bg-white/10 border border-white/20 text-white"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1">Largeur ticket</label>
                  <select
                    value={printerConfig.ticketWidth}
                    onChange={(e) => setPrinterConfig({...printerConfig, ticketWidth: parseInt(e.target.value)})}
                    className="w-full h-10 px-3 rounded-md bg-white/10 border border-white/20 text-white"
                  >
                    <option value="58" className="bg-slate-800">58mm (standard)</option>
                    <option value="80" className="bg-slate-800">80mm (large)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1">Taille police</label>
                  <select
                    value={printerConfig.fontSize}
                    onChange={(e) => setPrinterConfig({...printerConfig, fontSize: parseInt(e.target.value)})}
                    className="w-full h-10 px-3 rounded-md bg-white/10 border border-white/20 text-white"
                  >
                    <option value="10" className="bg-slate-800">Petit (10px)</option>
                    <option value="12" className="bg-slate-800">Normal (12px)</option>
                    <option value="14" className="bg-slate-800">Grand (14px)</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Message de fin</label>
                <input
                  type="text"
                  value={printerConfig.footerMessage}
                  onChange={(e) => setPrinterConfig({...printerConfig, footerMessage: e.target.value})}
                  className="w-full h-10 px-3 rounded-md bg-white/10 border border-white/20 text-white"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button 
                variant="outline" 
                onClick={() => setShowPrinterModal(false)} 
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Annuler
              </Button>
              <Button 
                onClick={() => {
                  savePrinterConfig(printerConfig);
                  setShowPrinterModal(false);
                }}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600"
              >
                <Save className="w-4 h-4 mr-2" />
                Sauvegarder
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Paiement Multiple */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" data-testid="payment-modal">
          <div className="bg-slate-800 rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 border border-white/20 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Wallet className="w-5 h-5 text-emerald-400" />
                Mode de Paiement
              </h2>
              <button 
                onClick={() => setShowPaymentModal(false)} 
                className="text-white/40 hover:text-white"
                data-testid="close-payment-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Montant total */}
            <div className="mb-6 p-4 rounded-lg bg-emerald-500/20 border border-emerald-500/30 text-center">
              <p className="text-sm text-emerald-300 mb-1">Montant à encaisser</p>
              <p className="text-3xl font-bold text-white">
                {formatCurrency(parseFloat(newEntry.sale_price || 0) * parseFloat(newEntry.quantity || 1))}
              </p>
            </div>
            
            {/* Options de paiement */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {/* Espèces */}
              <button
                onClick={() => setPaymentModalData({...paymentModalData, mode: 'cash'})}
                data-testid="payment-option-cash"
                className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                  paymentModalData.mode === 'cash'
                    ? 'bg-emerald-500/30 border-emerald-400 text-white'
                    : 'bg-white/5 border-white/20 text-white/70 hover:bg-white/10'
                }`}
              >
                <Banknote className="w-8 h-8" />
                <span className="font-bold">Espèces</span>
                <span className="text-xs opacity-70">100% liquide</span>
              </button>

              {/* Carte Bancaire */}
              <button
                onClick={() => setPaymentModalData({...paymentModalData, mode: 'card'})}
                data-testid="payment-option-card"
                className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                  paymentModalData.mode === 'card'
                    ? 'bg-sky-500/30 border-sky-400 text-white'
                    : 'bg-white/5 border-white/20 text-white/70 hover:bg-white/10'
                }`}
              >
                <CreditCard className="w-8 h-8" />
                <span className="font-bold">Carte Bancaire</span>
                <span className="text-xs opacity-70">100% CB</span>
              </button>

              {/* Mixte */}
              <button
                onClick={() => setPaymentModalData({...paymentModalData, mode: 'mixed'})}
                data-testid="payment-option-mixed"
                className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                  paymentModalData.mode === 'mixed'
                    ? 'bg-purple-500/30 border-purple-400 text-white'
                    : 'bg-white/5 border-white/20 text-white/70 hover:bg-white/10'
                }`}
              >
                <Split className="w-8 h-8" />
                <span className="font-bold">Mixte</span>
                <span className="text-xs opacity-70">CB + Espèces + Points/Avoir</span>
              </button>

              {/* Échelonné */}
              <button
                onClick={() => setPaymentModalData({...paymentModalData, mode: 'installment'})}
                data-testid="payment-option-installment"
                className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                  paymentModalData.mode === 'installment'
                    ? 'bg-amber-500/30 border-amber-400 text-white'
                    : 'bg-white/5 border-white/20 text-white/70 hover:bg-white/10'
                }`}
              >
                <Clock className="w-8 h-8" />
                <span className="font-bold">Échelonné</span>
                <span className="text-xs opacity-70">Plusieurs fois</span>
              </button>
            </div>

            {/* Options supplémentaires selon le mode */}
            {paymentModalData.mode === 'mixed' && (
              <div className="mb-6 space-y-4">
                {/* Répartition Espèces / CB - EN PREMIER */}
                <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/30 space-y-4">
                  <h3 className="font-bold text-white flex items-center gap-2">
                    <Split className="w-4 h-4 text-purple-400" />
                    Répartition Espèces / Carte
                  </h3>
                  
                  {/* Affichage du reste à payer */}
                  {(() => {
                    const totalSale = parseFloat(newEntry.sale_price || 0) * parseFloat(newEntry.quantity || 1);
                    const pointsDed = paymentModalData.usePoints ? parseFloat(paymentModalData.pointsAmount || 0) : 0;
                    const avoirDed = paymentModalData.useAvoir ? parseFloat(paymentModalData.avoirAmount || 0) : 0;
                    const promoDed = paymentModalData.usePromo ? parseFloat(paymentModalData.promoAmount || 0) : 0;
                    const remaining = Math.max(0, totalSale - pointsDed - avoirDed - promoDed);
                    
                    return (pointsDed > 0 || avoirDed > 0 || promoDed > 0) && (
                      <div className="p-3 rounded-lg bg-amber-500/20 border border-amber-500/50 mb-4">
                        <div className="flex justify-between items-center text-white">
                          <span>Total achat:</span>
                          <span className="font-bold">{totalSale.toFixed(2)}€</span>
                        </div>
                        {pointsDed > 0 && (
                          <div className="flex justify-between items-center text-amber-300">
                            <span>- Remise points:</span>
                            <span>-{pointsDed.toFixed(2)}€</span>
                          </div>
                        )}
                        {avoirDed > 0 && (
                          <div className="flex justify-between items-center text-blue-300">
                            <span>- Avoir:</span>
                            <span>-{avoirDed.toFixed(2)}€</span>
                          </div>
                        )}
                        {promoDed > 0 && (
                          <div className="flex justify-between items-center text-green-300">
                            <span>- Promo:</span>
                            <span>-{promoDed.toFixed(2)}€</span>
                          </div>
                        )}
                        <div className="flex justify-between items-center text-white font-bold text-lg border-t border-white/20 mt-2 pt-2">
                          <span>Reste à payer:</span>
                          <span className="text-emerald-400">{remaining.toFixed(2)}€</span>
                        </div>
                        
                        {/* Boutons rapides */}
                        <div className="flex gap-2 mt-3">
                          <button
                            type="button"
                            onClick={() => setPaymentModalData(prev => ({
                              ...prev,
                              cashAmount: remaining.toFixed(2),
                              cardAmount: '0'
                            }))}
                            className="flex-1 py-2 px-3 rounded bg-emerald-500/30 hover:bg-emerald-500/50 text-emerald-300 text-sm font-medium flex items-center justify-center gap-1"
                          >
                            <Banknote className="w-4 h-4" />
                            Tout en espèces
                          </button>
                          <button
                            type="button"
                            onClick={() => setPaymentModalData(prev => ({
                              ...prev,
                              cashAmount: '0',
                              cardAmount: remaining.toFixed(2)
                            }))}
                            className="flex-1 py-2 px-3 rounded bg-sky-500/30 hover:bg-sky-500/50 text-sky-300 text-sm font-medium flex items-center justify-center gap-1"
                          >
                            <CreditCard className="w-4 h-4" />
                            Tout en carte
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-white/80 mb-1 flex items-center gap-1">
                        <Banknote className="w-4 h-4 text-emerald-400" /> Espèces
                      </label>
                      <input
                        type="number"
                        value={paymentModalData.cashAmount}
                        onChange={(e) => {
                          const cash = parseFloat(e.target.value) || 0;
                          const totalSale = parseFloat(newEntry.sale_price || 0) * parseFloat(newEntry.quantity || 1);
                          const pointsDed = paymentModalData.usePoints ? parseFloat(paymentModalData.pointsAmount || 0) : 0;
                          const avoirDed = paymentModalData.useAvoir ? parseFloat(paymentModalData.avoirAmount || 0) : 0;
                          const promoDed = paymentModalData.usePromo ? parseFloat(paymentModalData.promoAmount || 0) : 0;
                          const remaining = totalSale - pointsDed - avoirDed - promoDed;
                          setPaymentModalData({
                            ...paymentModalData,
                            cashAmount: e.target.value,
                            cardAmount: Math.max(0, remaining - cash).toFixed(2)
                          });
                        }}
                        placeholder="0.00"
                        data-testid="mixed-cash-input"
                        className="w-full h-12 px-3 rounded-md bg-white/10 border border-emerald-500/50 text-white text-lg font-bold text-center"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-white/80 mb-1 flex items-center gap-1">
                        <CreditCard className="w-4 h-4 text-sky-400" /> Carte
                      </label>
                      <input
                        type="number"
                        value={paymentModalData.cardAmount}
                        onChange={(e) => {
                          const card = parseFloat(e.target.value) || 0;
                          const totalSale = parseFloat(newEntry.sale_price || 0) * parseFloat(newEntry.quantity || 1);
                          const pointsDed = paymentModalData.usePoints ? parseFloat(paymentModalData.pointsAmount || 0) : 0;
                          const avoirDed = paymentModalData.useAvoir ? parseFloat(paymentModalData.avoirAmount || 0) : 0;
                          const promoDed = paymentModalData.usePromo ? parseFloat(paymentModalData.promoAmount || 0) : 0;
                          const remaining = totalSale - pointsDed - avoirDed - promoDed;
                          setPaymentModalData({
                            ...paymentModalData,
                            cardAmount: e.target.value,
                            cashAmount: Math.max(0, remaining - card).toFixed(2)
                          });
                        }}
                        placeholder="0.00"
                        data-testid="mixed-card-input"
                        className="w-full h-12 px-3 rounded-md bg-white/10 border border-sky-500/50 text-white text-lg font-bold text-center"
                      />
                    </div>
                  </div>
                  {/* Validation visuelle */}
                  {(() => {
                    const totalSale = parseFloat(newEntry.sale_price || 0) * parseFloat(newEntry.quantity || 1);
                    const pointsDed = paymentModalData.usePoints ? parseFloat(paymentModalData.pointsAmount || 0) : 0;
                    const avoirDed = paymentModalData.useAvoir ? parseFloat(paymentModalData.avoirAmount || 0) : 0;
                    const promoDed = paymentModalData.usePromo ? parseFloat(paymentModalData.promoAmount || 0) : 0;
                    const remaining = Math.max(0, totalSale - pointsDed - avoirDed - promoDed);
                    const cashCard = parseFloat(paymentModalData.cashAmount || 0) + parseFloat(paymentModalData.cardAmount || 0);
                    const isValid = Math.abs(cashCard - remaining) < 0.01;
                    
                    return (paymentModalData.cashAmount || paymentModalData.cardAmount) && (
                      <div className={`text-center p-2 rounded ${isValid ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>
                        Total: {formatCurrency(cashCard)}
                        {!isValid && (
                          <span className="ml-2">(attendu: {formatCurrency(remaining)})</span>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Section Remises (Points, Avoir, Promo) - OPTIONNEL */}
                <details className="group">
                  <summary className="cursor-pointer p-3 rounded-lg bg-violet-500/10 border border-violet-500/30 flex items-center justify-between hover:bg-violet-500/20 transition-colors">
                    <span className="font-bold text-white flex items-center gap-2">
                      <Gift className="w-4 h-4 text-violet-400" />
                      Remises & Avantages (optionnel)
                    </span>
                    <span className="text-violet-400 group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <div className="mt-2 p-4 rounded-lg bg-violet-500/10 border border-violet-500/30 space-y-4">
                    {/* Recherche client pour utilisation points/promo */}
                    <div className="relative">
                      <label className="block text-sm text-white/80 mb-1 flex items-center gap-1">
                        <User className="w-4 h-4 text-violet-400" /> Client (obligatoire pour points/promo)
                      </label>
                      {paymentModalData.selectedClient ? (
                        <div className="flex items-center justify-between p-3 rounded-md bg-violet-500/20 border border-violet-500/50">
                          <div>
                            <p className="font-medium text-white">{paymentModalData.selectedClient.name}</p>
                            <p className="text-sm text-violet-300">
                              {paymentModalData.selectedClient.points_balance || 0} pts disponibles
                              {paymentModalData.selectedClient.points_balance >= 100 && (
                                <span className="ml-2 text-green-400">
                                  (= {Math.floor((paymentModalData.selectedClient.points_balance || 0) / 20)}€ max)
                                </span>
                              )}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setPaymentModalData(prev => ({ ...prev, selectedClient: null, usePoints: false, pointsAmount: '', usePromo: false, promoAmount: '' }));
                              setMixedPaymentClientSearch('');
                            }}
                            className="text-white/60 hover:text-white"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                            <input
                              type="text"
                              placeholder="Rechercher un client..."
                              value={mixedPaymentClientSearch}
                              onChange={(e) => setMixedPaymentClientSearch(e.target.value)}
                              className="w-full h-10 pl-10 pr-3 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/40"
                            />
                          </div>
                          {mixedPaymentSearching && (
                            <p className="text-xs text-white/60 mt-1">Recherche...</p>
                          )}
                          {mixedPaymentSearchResults.length > 0 && (
                            <div className="absolute z-50 left-0 right-0 mt-1 bg-slate-800 border border-white/20 rounded-lg shadow-xl max-h-40 overflow-y-auto">
                              {mixedPaymentSearchResults.map(client => (
                                <button
                                  key={client.id}
                                  onClick={() => {
                                    setPaymentModalData(prev => ({ ...prev, selectedClient: client }));
                                    setMixedPaymentClientSearch('');
                                    setMixedPaymentSearchResults([]);
                                  }}
                                  className="w-full text-left px-4 py-2 hover:bg-white/10 border-b border-white/10 last:border-0"
                                >
                                  <p className="font-medium text-white">{client.name}</p>
                                  <p className="text-xs text-white/60">
                                    {client.phone} • {client.points_balance || 0} pts
                                  </p>
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Options de remise en ligne */}
                    <div className="grid grid-cols-3 gap-2">
                      {/* Points */}
                      <div className={`p-2 rounded-lg border text-center ${paymentModalData.usePoints ? 'bg-amber-500/20 border-amber-500/50' : 'bg-white/5 border-white/10'}`}>
                        <label className="flex flex-col items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={paymentModalData.usePoints}
                            onChange={(e) => {
                              setPaymentModalData(prev => ({ 
                                ...prev, 
                                usePoints: e.target.checked, 
                                pointsAmount: ''
                              }));
                            }}
                            disabled={!paymentModalData.selectedClient}
                            className="w-4 h-4 rounded"
                          />
                          <Star className="w-4 h-4 text-amber-400" />
                          <span className="text-white text-xs">Points</span>
                        </label>
                        {paymentModalData.usePoints && paymentModalData.selectedClient && (
                          <input
                            type="number"
                            value={paymentModalData.pointsAmount}
                            onChange={(e) => {
                              const maxPoints = Math.floor((paymentModalData.selectedClient?.points_balance || 0) / 20);
                              const totalSale = parseFloat(newEntry.sale_price || 0) * parseFloat(newEntry.quantity || 1);
                              const value = Math.min(parseFloat(e.target.value) || 0, maxPoints, totalSale);
                              setPaymentModalData(prev => ({ 
                                ...prev, 
                                pointsAmount: value > 0 ? value.toString() : ''
                              }));
                            }}
                            placeholder="€"
                            className="w-full h-8 mt-1 px-2 rounded bg-white/10 border border-amber-500/50 text-white text-center text-sm"
                          />
                        )}
                      </div>

                      {/* Avoir */}
                      <div className={`p-2 rounded-lg border text-center ${paymentModalData.useAvoir ? 'bg-blue-500/20 border-blue-500/50' : 'bg-white/5 border-white/10'}`}>
                        <label className="flex flex-col items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={paymentModalData.useAvoir}
                            onChange={(e) => setPaymentModalData(prev => ({ ...prev, useAvoir: e.target.checked, avoirAmount: '' }))}
                            className="w-4 h-4 rounded"
                          />
                          <Wallet className="w-4 h-4 text-blue-400" />
                          <span className="text-white text-xs">Avoir</span>
                        </label>
                        {paymentModalData.useAvoir && (
                          <input
                            type="number"
                            value={paymentModalData.avoirAmount}
                            onChange={(e) => setPaymentModalData(prev => ({ ...prev, avoirAmount: e.target.value }))}
                            placeholder="€"
                            className="w-full h-8 mt-1 px-2 rounded bg-white/10 border border-blue-500/50 text-white text-center text-sm"
                          />
                        )}
                      </div>

                      {/* Promo */}
                      <div className={`p-2 rounded-lg border text-center ${paymentModalData.usePromo ? 'bg-green-500/20 border-green-500/50' : 'bg-white/5 border-white/10'}`}>
                        <label className="flex flex-col items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={paymentModalData.usePromo}
                            onChange={(e) => setPaymentModalData(prev => ({ ...prev, usePromo: e.target.checked, promoAmount: '', promoDescription: '' }))}
                            disabled={!paymentModalData.selectedClient}
                            className="w-4 h-4 rounded"
                          />
                          <Gift className="w-4 h-4 text-green-400" />
                          <span className="text-white text-xs">Promo</span>
                        </label>
                        {paymentModalData.usePromo && paymentModalData.selectedClient && (
                          <input
                            type="number"
                            value={paymentModalData.promoAmount}
                            onChange={(e) => setPaymentModalData(prev => ({ ...prev, promoAmount: e.target.value }))}
                            placeholder="€"
                            className="w-full h-8 mt-1 px-2 rounded bg-white/10 border border-green-500/50 text-white text-center text-sm"
                          />
                        )}
                      </div>
                    </div>

                    {/* Résumé des déductions */}
                    {(() => {
                      const totalSale = parseFloat(newEntry.sale_price || 0) * parseFloat(newEntry.quantity || 1);
                      const pointsDed = paymentModalData.usePoints ? parseFloat(paymentModalData.pointsAmount || 0) : 0;
                      const avoirDed = paymentModalData.useAvoir ? parseFloat(paymentModalData.avoirAmount || 0) : 0;
                      const promoDed = paymentModalData.usePromo ? parseFloat(paymentModalData.promoAmount || 0) : 0;
                      const totalDeductions = pointsDed + avoirDed + promoDed;
                      
                      return totalDeductions > 0 && (
                        <div className="p-2 rounded bg-violet-500/20 text-sm">
                          <div className="flex justify-between">
                            <span className="text-white/80">Remises totales:</span>
                            <span className="text-violet-300 font-bold">-{formatCurrency(totalDeductions)}</span>
                          </div>
                          <div className="flex justify-between border-t border-white/20 mt-1 pt-1">
                            <span className="text-white">Reste à payer:</span>
                            <span className="text-white font-bold">{formatCurrency(Math.max(0, totalSale - totalDeductions))}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </details>
              </div>
            )}

            {paymentModalData.mode === 'installment' && (
              <div className="mb-6 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 space-y-4">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-400" />
                  Paiement échelonné
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-white/80 mb-1">Nombre de versements</label>
                    <select
                      value={paymentModalData.installmentCount}
                      onChange={(e) => setPaymentModalData({
                        ...paymentModalData, 
                        installmentCount: parseInt(e.target.value),
                        installmentPaid: Math.min(paymentModalData.installmentPaid, parseInt(e.target.value))
                      })}
                      data-testid="installment-count-select"
                      className="w-full h-12 px-3 rounded-md bg-white/10 border border-amber-500/50 text-white text-lg font-bold"
                    >
                      <option value="2" className="bg-slate-800">2 fois</option>
                      <option value="3" className="bg-slate-800">3 fois</option>
                      <option value="4" className="bg-slate-800">4 fois</option>
                      <option value="5" className="bg-slate-800">5 fois</option>
                      <option value="6" className="bg-slate-800">6 fois</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-white/80 mb-1">Versements payés</label>
                    <select
                      value={paymentModalData.installmentPaid}
                      onChange={(e) => setPaymentModalData({...paymentModalData, installmentPaid: parseInt(e.target.value)})}
                      data-testid="installment-paid-select"
                      className="w-full h-12 px-3 rounded-md bg-white/10 border border-amber-500/50 text-white text-lg font-bold"
                    >
                      {Array.from({length: paymentModalData.installmentCount}, (_, i) => (
                        <option key={i+1} value={i+1} className="bg-slate-800">{i+1} / {paymentModalData.installmentCount}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {/* Résumé */}
                <div className="bg-white/5 rounded-lg p-3">
                  <div className="flex justify-between text-white/80 mb-1">
                    <span>Montant par versement:</span>
                    <span className="font-bold text-white">
                      {formatCurrency((parseFloat(newEntry.sale_price || 0) * parseFloat(newEntry.quantity || 1)) / paymentModalData.installmentCount)}
                    </span>
                  </div>
                  <div className="flex justify-between text-white/80 mb-1">
                    <span>Déjà encaissé:</span>
                    <span className="font-bold text-emerald-400">
                      {formatCurrency((parseFloat(newEntry.sale_price || 0) * parseFloat(newEntry.quantity || 1)) / paymentModalData.installmentCount * paymentModalData.installmentPaid)}
                    </span>
                  </div>
                  <div className="flex justify-between text-white/80 border-t border-white/10 pt-2 mt-2">
                    <span>Reste à encaisser:</span>
                    <span className="font-bold text-amber-400">
                      {formatCurrency((parseFloat(newEntry.sale_price || 0) * parseFloat(newEntry.quantity || 1)) - ((parseFloat(newEntry.sale_price || 0) * parseFloat(newEntry.quantity || 1)) / paymentModalData.installmentCount * paymentModalData.installmentPaid))}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Boutons d'action */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowPaymentModal(false)} 
                className="flex-1 border-white/20 text-white hover:bg-white/10"
                data-testid="cancel-payment-btn"
              >
                Annuler
              </Button>
              <Button 
                onClick={confirmPaymentMethod}
                disabled={!paymentModalData.mode}
                className={`flex-1 font-bold ${
                  paymentModalData.mode === 'cash' ? 'bg-emerald-500 hover:bg-emerald-600' :
                  paymentModalData.mode === 'card' ? 'bg-sky-500 hover:bg-sky-600' :
                  paymentModalData.mode === 'mixed' ? 'bg-purple-500 hover:bg-purple-600' :
                  paymentModalData.mode === 'installment' ? 'bg-amber-500 hover:bg-amber-600' :
                  'bg-gray-500'
                }`}
                data-testid="confirm-payment-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                Confirmer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'annulation avec motif obligatoire */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" data-testid="cancel-modal">
          <div className="bg-slate-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6 border border-red-500/30">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-full bg-red-500/20">
                <X className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Annuler cette entrée ?</h2>
                <p className="text-white/60 text-sm">Cette action est irréversible</p>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-white/80 mb-2">
                Motif d'annulation <span className="text-red-400">*</span>
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Ex: Erreur de saisie, Client a changé d'avis, Doublon..."
                className="w-full h-24 px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:border-red-400 resize-none"
                data-testid="cancel-reason-input"
                autoFocus
              />
              <p className="text-xs text-white/50 mt-1">Minimum 3 caractères requis</p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancellingEntryId(null);
                  setCancelReason('');
                }}
                variant="outline"
                className="flex-1 bg-white/10 border-white/20 text-white hover:bg-white/20"
                data-testid="cancel-modal-close"
              >
                Annuler
              </Button>
              <Button
                onClick={handleConfirmCancel}
                disabled={!cancelReason || cancelReason.trim().length < 3}
                className={`flex-1 ${
                  cancelReason && cancelReason.trim().length >= 3
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                }`}
                data-testid="confirm-cancel-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Confirmer l'annulation
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Import Document Rapide avec IA */}
      {showDocumentModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Upload className="w-5 h-5 text-indigo-400" />
                  Nouveau document comptable
                </h2>
                <Button onClick={() => {
                  setShowDocumentModal(false);
                  setAiAnalysisResult(null);
                }} variant="ghost" size="sm" className="text-gray-400">
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="space-y-4">
                {/* Section 1: Import fichier */}
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <h3 className="text-sm font-semibold text-white/80 mb-3">1. Document à importer</h3>
                  <div className="flex gap-2">
                    <input
                      type="file"
                      id="doc-file-input"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          if (file.size > 10 * 1024 * 1024) {
                            toast.error('Fichier trop volumineux (max 10 Mo)');
                            return;
                          }
                          const reader = new FileReader();
                          reader.onload = async (event) => {
                            const base64Data = event.target.result.split(',')[1];
                            setDocumentForm(prev => ({
                              ...prev,
                              file_data: base64Data,
                              file_name: file.name,
                              file_type: file.type,
                              title: prev.title || file.name.replace(/\.[^/.]+$/, '')
                            }));
                            setAiAnalysisResult(null);
                            toast.success(`"${file.name}" sélectionné`);
                            
                            // Analyse automatique si activée
                            if (autoAnalyze) {
                              setAnalyzingDocument(true);
                              try {
                                const response = await axios.post(`${BACKEND_URL}/api/ai-document/analyze`, {
                                  file_data: base64Data,
                                  file_name: file.name,
                                  file_type: file.type
                                }, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                
                                if (response.data.success) {
                                  setAiAnalysisResult(response.data);
                                  const data = response.data.extracted_data;
                                  
                                  setDocumentForm(prev => ({
                                    ...prev,
                                    title: data.description || data.fournisseur || prev.title,
                                    amount: data.montant || prev.amount,
                                    date: data.date || prev.date,
                                    supplier: data.fournisseur || prev.supplier,
                                    reference: data.reference || prev.reference,
                                    category_id: response.data.category_id || prev.category_id
                                  }));
                                  
                                  toast.success(`✅ Analysé ! Confiance: ${data.confiance}/10 - Vérifiez et cliquez sur Enregistrer`, {
                                    duration: 5000
                                  });
                                }
                              } catch (error) {
                                console.error('Erreur analyse:', error);
                                toast.error('Analyse IA échouée - Remplissez manuellement');
                              } finally {
                                setAnalyzingDocument(false);
                              }
                            }
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      accept="image/*,.pdf,.xlsx,.xls,.csv"
                      className="hidden"
                    />
                    <input
                      type="file"
                      id="doc-camera-input"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = async (event) => {
                            const base64Data = event.target.result.split(',')[1];
                            setDocumentForm(prev => ({
                              ...prev,
                              file_data: base64Data,
                              file_name: file.name,
                              file_type: file.type,
                              title: prev.title || `Scan_${new Date().toISOString().slice(0,10)}`
                            }));
                            setAiAnalysisResult(null);
                            toast.success('Photo capturée');
                            
                            // Analyse automatique
                            if (autoAnalyze) {
                              setAnalyzingDocument(true);
                              try {
                                const response = await axios.post(`${BACKEND_URL}/api/ai-document/analyze`, {
                                  file_data: base64Data,
                                  file_name: file.name,
                                  file_type: file.type
                                }, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                
                                if (response.data.success) {
                                  setAiAnalysisResult(response.data);
                                  const data = response.data.extracted_data;
                                  
                                  setDocumentForm(prev => ({
                                    ...prev,
                                    title: data.description || data.fournisseur || prev.title,
                                    amount: data.montant || prev.amount,
                                    date: data.date || prev.date,
                                    supplier: data.fournisseur || prev.supplier,
                                    reference: data.reference || prev.reference,
                                    category_id: response.data.category_id || prev.category_id
                                  }));
                                  
                                  toast.success(`✅ Analysé ! Confiance: ${data.confiance}/10`, { duration: 5000 });
                                }
                              } catch (error) {
                                toast.error('Analyse IA échouée');
                              } finally {
                                setAnalyzingDocument(false);
                              }
                            }
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                    />
                    
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 border-white/20 text-white"
                      onClick={() => document.getElementById('doc-file-input').click()}
                    >
                      <FolderOpen className="w-4 h-4 mr-2" />
                      Fichier
                    </Button>
                    
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 border-white/20 text-white"
                      onClick={() => document.getElementById('doc-camera-input').click()}
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Scanner
                    </Button>
                  </div>
                  
                  {documentForm.file_name && (
                    <div className="mt-2 p-2 bg-green-500/20 rounded-lg text-green-300 text-sm flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      {documentForm.file_name}
                      {analyzingDocument && <Loader2 className="w-4 h-4 animate-spin ml-auto" />}
                    </div>
                  )}
                  
                  {/* Checkbox analyse auto */}
                  <div className="mt-3 flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="auto-analyze-doc"
                      checked={autoAnalyze}
                      onChange={(e) => setAutoAnalyze(e.target.checked)}
                      className="rounded border-white/30"
                    />
                    <label htmlFor="auto-analyze-doc" className="text-sm text-white/60">
                      Analyse automatique (Claude IA)
                    </label>
                  </div>
                </div>

                {/* Résultat IA */}
                {aiAnalysisResult && (
                  <div className="bg-gradient-to-r from-purple-500/10 to-indigo-500/10 border border-purple-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-purple-300 text-sm font-medium mb-3">
                      <Sparkles className="w-4 h-4" />
                      Analyse IA
                      <span className="ml-auto bg-purple-500/30 px-2 py-0.5 rounded text-xs">
                        Confiance: {aiAnalysisResult.extracted_data.confiance}/10
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {aiAnalysisResult.extracted_data.montant && (
                        <div>
                          <span className="text-white/50">Montant:</span>
                          <span className="text-green-400 ml-2 font-medium">{aiAnalysisResult.extracted_data.montant}€</span>
                        </div>
                      )}
                      {aiAnalysisResult.extracted_data.fournisseur && (
                        <div>
                          <span className="text-white/50">Fournisseur:</span>
                          <span className="text-white ml-2">{aiAnalysisResult.extracted_data.fournisseur}</span>
                        </div>
                      )}
                      {aiAnalysisResult.suggested_category && (
                        <div className="col-span-2">
                          <span className="text-white/50">Catégorie:</span>
                          <span className="text-purple-300 ml-2">{aiAnalysisResult.suggested_category}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Section 2: Formulaire */}
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <h3 className="text-sm font-semibold text-white/80 mb-3">2. Vérifier et compléter</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="text-sm text-white/70 mb-1 block">Titre *</label>
                      <Input
                        value={documentForm.title}
                        onChange={(e) => setDocumentForm({...documentForm, title: e.target.value})}
                        placeholder="Ex: Facture Orange, Loyer..."
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm text-white/70 mb-1 block">Catégorie</label>
                      <select
                        value={documentForm.category_id}
                        onChange={(e) => setDocumentForm({...documentForm, category_id: e.target.value})}
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                      >
                        {accountingCategories.length > 0 ? (
                          accountingCategories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))
                        ) : (
                          <>
                            <option value="cat_achat">Achats / Stock</option>
                            <option value="cat_charges">Charges fixes</option>
                            <option value="cat_frais">Frais boutique</option>
                            <option value="cat_amenagement">Aménagements</option>
                            <option value="cat_transport">Transport</option>
                            <option value="cat_telecom">Télécom / Internet</option>
                            <option value="cat_assurance">Assurances</option>
                            <option value="cat_autre">Autres</option>
                          </>
                        )}
                      </select>
                    </div>
                    
                    <div>
                      <label className="text-sm text-white/70 mb-1 block">Montant TTC (€)</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={documentForm.amount}
                        onChange={(e) => setDocumentForm({...documentForm, amount: e.target.value})}
                        placeholder="0.00"
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm text-white/70 mb-1 block">Date *</label>
                      <Input
                        type="date"
                        value={documentForm.date}
                        onChange={(e) => setDocumentForm({...documentForm, date: e.target.value})}
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm text-white/70 mb-1 block">Fournisseur</label>
                      <Input
                        value={documentForm.supplier}
                        onChange={(e) => setDocumentForm({...documentForm, supplier: e.target.value})}
                        placeholder="Ex: Orange, EDF..."
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm text-white/70 mb-1 block">Référence</label>
                      <Input
                        value={documentForm.reference}
                        onChange={(e) => setDocumentForm({...documentForm, reference: e.target.value})}
                        placeholder="N° facture..."
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <label className="text-sm text-white/70 mb-1 block">Notes</label>
                      <textarea
                        value={documentForm.notes}
                        onChange={(e) => setDocumentForm({...documentForm, notes: e.target.value})}
                        placeholder="Notes..."
                        rows={2}
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm resize-none"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Boutons */}
              <div className="flex gap-3 mt-6">
                <Button onClick={() => {
                  setShowDocumentModal(false);
                  setAiAnalysisResult(null);
                }} variant="outline" className="flex-1 border-white/20 text-white">
                  Annuler
                </Button>
                <Button 
                  onClick={saveDocumentFromModal}
                  disabled={savingDocument || !documentForm.title}
                  className={`flex-1 ${
                    savingDocument ? 'bg-gray-500' : aiAnalysisResult ? 'bg-emerald-500 hover:bg-emerald-600 animate-pulse' : 'bg-indigo-500 hover:bg-indigo-600'
                  }`}
                >
                  {savingDocument ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {aiAnalysisResult ? '⬇️ Enregistrer' : 'Enregistrer'}
                    </>
                  )}
                </Button>
              </div>
              
              {/* Rappel après analyse */}
              {aiAnalysisResult && (
                <div className="mt-3 bg-emerald-500/20 border border-emerald-500/40 rounded-lg p-3 text-center">
                  <p className="text-emerald-300 text-sm font-medium">
                    ✅ Document analysé ! Cliquez sur "Enregistrer" pour sauvegarder.
                  </p>
                </div>
              )}
              
              {/* Info IA */}
              <div className="mt-4 text-center text-xs text-white/40">
                🔒 IA Claude - Vos documents restent privés
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL FIDÉLITÉ ENCAISSEMENT ===== */}
      {showLoyaltyModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowLoyaltyModal(false)}>
          <div className="bg-slate-800 rounded-2xl w-full max-w-lg p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <User className="w-6 h-6 text-amber-400" />
                Attribution Fidélité
              </h2>
              <button onClick={() => setShowLoyaltyModal(false)} className="text-white/50 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Info de la ligne */}
            {loyaltyEntryId && entries.find(e => e.id === loyaltyEntryId) && (
              <div className="bg-slate-700/50 rounded-lg p-3 mb-4">
                <p className="text-white font-medium">{entries.find(e => e.id === loyaltyEntryId)?.description}</p>
                <p className="text-amber-400 font-bold text-lg">
                  Total: {formatCurrency(entries.find(e => e.id === loyaltyEntryId)?.sale_price * entries.find(e => e.id === loyaltyEntryId)?.quantity)}
                  <span className="text-sm font-normal text-white/60 ml-2">
                    → +{Math.floor(entries.find(e => e.id === loyaltyEntryId)?.sale_price * entries.find(e => e.id === loyaltyEntryId)?.quantity)} points
                  </span>
                </p>
              </div>
            )}

            {/* Options de fidélité */}
            <div className="space-y-3 mb-6">
              {/* Option 1: Compte existant */}
              <button
                onClick={() => setLoyaltyMode('existing')}
                className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${
                  loyaltyMode === 'existing'
                    ? 'border-green-500 bg-green-500/20 text-white'
                    : 'border-white/20 bg-white/5 text-white/80 hover:bg-white/10'
                }`}
              >
                <div className="w-12 h-12 rounded-full bg-green-500/30 flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-green-400" />
                </div>
                <div className="text-left flex-1">
                  <p className="font-bold">Compte existant</p>
                  <p className="text-sm text-white/60">Rechercher et sélectionner un client</p>
                </div>
              </button>

              {/* Option 2: Pas de compte */}
              <button
                onClick={() => {
                  setLoyaltyMode('none');
                  setLoyaltySelectedClient(null);
                }}
                className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${
                  loyaltyMode === 'none'
                    ? 'border-gray-500 bg-gray-500/20 text-white'
                    : 'border-white/20 bg-white/5 text-white/80 hover:bg-white/10'
                }`}
              >
                <div className="w-12 h-12 rounded-full bg-gray-500/30 flex items-center justify-center">
                  <X className="w-6 h-6 text-gray-400" />
                </div>
                <div className="text-left flex-1">
                  <p className="font-bold">Pas de compte</p>
                  <p className="text-sm text-white/60">Ignorer la distribution des points</p>
                </div>
              </button>

              {/* Option 3: Nouveau compte */}
              <button
                onClick={() => setLoyaltyMode('new')}
                className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${
                  loyaltyMode === 'new'
                    ? 'border-blue-500 bg-blue-500/20 text-white'
                    : 'border-white/20 bg-white/5 text-white/80 hover:bg-white/10'
                }`}
              >
                <div className="w-12 h-12 rounded-full bg-blue-500/30 flex items-center justify-center">
                  <UserPlus className="w-6 h-6 text-blue-400" />
                </div>
                <div className="text-left flex-1">
                  <p className="font-bold">Nouveau compte</p>
                  <p className="text-sm text-white/60">Créer une nouvelle fiche client</p>
                </div>
              </button>
            </div>

            {/* Contenu selon le mode sélectionné */}
            {loyaltyMode === 'existing' && (
              <div className="space-y-4 border-t border-white/10 pt-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 w-5 h-5" />
                  <Input
                    placeholder="Rechercher par nom, prénom ou téléphone..."
                    value={loyaltyClientSearch}
                    onChange={(e) => setLoyaltyClientSearch(e.target.value)}
                    className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    autoFocus
                  />
                </div>

                {/* Résultats de recherche */}
                {loyaltySearching && (
                  <div className="text-center py-4 text-white/50">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                    Recherche...
                  </div>
                )}

                {!loyaltySearching && loyaltySearchResults.length > 0 && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {loyaltySearchResults.map((client) => (
                      <button
                        key={client.id}
                        onClick={() => selectLoyaltyClient(client)}
                        className={`w-full p-3 rounded-lg border transition-all flex items-center justify-between ${
                          loyaltySelectedClient?.id === client.id
                            ? 'border-green-500 bg-green-500/20'
                            : 'border-white/10 bg-white/5 hover:bg-white/10'
                        }`}
                      >
                        <div className="text-left">
                          <p className="text-white font-medium">{client.name}</p>
                          <p className="text-white/50 text-sm flex items-center gap-2">
                            {client.phone && <span><Phone className="w-3 h-3 inline" /> {client.phone}</span>}
                            {client.email && <span><Mail className="w-3 h-3 inline" /> {client.email}</span>}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-amber-400 font-bold">{client.points_balance || 0} pts</p>
                          <p className="text-white/40 text-xs">{client.visit_count || 0} visites</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {!loyaltySearching && loyaltyClientSearch.length >= 2 && loyaltySearchResults.length === 0 && (
                  <p className="text-center text-white/50 py-4">Aucun client trouvé</p>
                )}

                {/* Client sélectionné */}
                {loyaltySelectedClient && (
                  <div className="bg-green-500/20 border border-green-500/40 rounded-lg p-3">
                    <p className="text-green-300 font-medium flex items-center gap-2">
                      <UserCheck className="w-4 h-4" />
                      Client sélectionné: {loyaltySelectedClient.name}
                    </p>
                    <p className="text-white/60 text-sm">
                      Solde actuel: {loyaltySelectedClient.points_balance || 0} pts
                    </p>
                  </div>
                )}
              </div>
            )}

            {loyaltyMode === 'new' && !loyaltySelectedClient && (
              <div className="space-y-4 border-t border-white/10 pt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-white/60 text-sm mb-1 block">Nom / Prénom *</label>
                    <Input
                      placeholder="Jean Dupont"
                      value={loyaltyNewClient.name}
                      onChange={(e) => setLoyaltyNewClient(prev => ({ ...prev, name: e.target.value }))}
                      className="bg-white/10 border-white/20 text-white"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="text-white/60 text-sm mb-1 block">Téléphone *</label>
                    <Input
                      type="tel"
                      placeholder="06 12 34 56 78"
                      value={loyaltyNewClient.phone}
                      onChange={(e) => setLoyaltyNewClient(prev => ({ ...prev, phone: e.target.value }))}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-white/60 text-sm mb-1 block">Email (facultatif)</label>
                  <Input
                    type="email"
                    placeholder="email@exemple.com"
                    value={loyaltyNewClient.email}
                    onChange={(e) => setLoyaltyNewClient(prev => ({ ...prev, email: e.target.value }))}
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>

                <div>
                  <label className="text-white/60 text-sm mb-1 block flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Adresse (recherche rapide)
                  </label>
                  <Input
                    placeholder="Commencez à taper une adresse..."
                    value={loyaltyNewClient.address}
                    onChange={(e) => {
                      setLoyaltyNewClient(prev => ({ ...prev, address: e.target.value }));
                      searchAddress(e.target.value);
                    }}
                    className="bg-white/10 border-white/20 text-white"
                  />
                  
                  {/* Suggestions d'adresses */}
                  {loyaltyAddresses.length > 0 && (
                    <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                      {loyaltyAddresses.map((addr, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setLoyaltyNewClient(prev => ({ ...prev, address: addr.label }));
                            setLoyaltyAddresses([]);
                          }}
                          className="w-full text-left p-2 rounded bg-white/5 hover:bg-white/10 text-white/80 text-sm"
                        >
                          {addr.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {loyaltyAddressLoading && (
                    <p className="text-white/40 text-sm mt-1">Recherche d'adresses...</p>
                  )}
                </div>

                <Button
                  onClick={handleCreateLoyaltyClient}
                  className="w-full bg-blue-500 hover:bg-blue-600"
                  disabled={!loyaltyNewClient.name.trim() || !loyaltyNewClient.phone.trim()}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Créer le client
                </Button>
              </div>
            )}

            {/* Client créé */}
            {loyaltyMode === 'new' && loyaltySelectedClient && (
              <div className="bg-blue-500/20 border border-blue-500/40 rounded-lg p-3 mt-4">
                <p className="text-blue-300 font-medium flex items-center gap-2">
                  <UserCheck className="w-4 h-4" />
                  Nouveau client créé: {loyaltySelectedClient.name}
                </p>
              </div>
            )}

            {/* Bouton de validation */}
            <div className="mt-6 pt-4 border-t border-white/10">
              <Button
                onClick={confirmLoyaltyChoice}
                disabled={!loyaltyMode || (loyaltyMode === 'existing' && !loyaltySelectedClient) || (loyaltyMode === 'new' && !loyaltySelectedClient)}
                className={`w-full h-12 font-bold text-lg ${
                  !loyaltyMode
                    ? 'bg-slate-600 opacity-50 cursor-not-allowed'
                    : loyaltyMode === 'none'
                      ? 'bg-gray-500 hover:bg-gray-600'
                      : 'bg-emerald-500 hover:bg-emerald-600'
                }`}
              >
                {!loyaltyMode ? (
                  <>
                    <User className="w-5 h-5 mr-2" />
                    Sélectionnez une option
                  </>
                ) : loyaltyMode === 'none' ? (
                  <>
                    <X className="w-5 h-5 mr-2" />
                    Ignorer la fidélité
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Attribuer les points
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}